import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import {
  FiAlertCircle,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiClock,
  FiCreditCard,
  FiExternalLink,
  FiFileText,
  FiKey,
  FiLock,
  FiMail,
  FiMapPin,
  FiMessageCircle,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUser,
  FiZap,
  FiX,
} from 'react-icons/fi';
import TermsModal from './TermsModal';
import PrivacyModal from './PrivacyModal';
import RegularCourseIntro from './RegularCourseIntro';

const REFERRAL_OPTIONS = [
  { value: 'sns', label: 'SNS' },
  { value: 'school', label: '학교 공문 / 선생님 추천' },
  { value: 'friend', label: '지인 소개' },
  { value: 'search', label: '검색' },
  { value: 'other', label: '기타' },
];

const USERNAME_PATTERN = /^[a-z_][-a-z0-9_]{0,31}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_PATTERN = /(https?:\/\/[^\s]+)/g;
const INITIAL_FORM_DATA = {
  applicantName: '',
  parentEmail: '',
  phoneNumber: '',
  childName: '',
  childGrade: '',
  childPhone: '',
  childEmail: '',
  username: '',
  password: '',
  confirmPassword: '',
  referralSource: '',
  referralOther: '',
  agreeTerms: false,
  agreePrivacy: false,
};

const FIELD_META = {
  applicantName: { section: '보호자 정보', label: '이름' },
  parentEmail: { section: '보호자 정보', label: '학부모 이메일' },
  phoneNumber: { section: '보호자 정보', label: '전화번호' },
  childName: { section: '자녀 정보', label: '자녀 이름' },
  childGrade: { section: '자녀 정보', label: '지역 및 학교/학년' },
  childPhone: { section: '자녀 정보', label: '자녀 전화번호' },
  childEmail: { section: '자녀 정보', label: '자녀 이메일' },
  username: { section: '계정 정보', label: '아이디' },
  password: { section: '계정 정보', label: '비밀번호' },
  confirmPassword: { section: '계정 정보', label: '비밀번호 확인' },
  referralSource: { section: '보호자 정보', label: '알게 된 경로' },
  referralOther: { section: '보호자 정보', label: '기타 경로' },
  agreeTerms: { section: '약관 동의', label: '서비스 이용약관' },
  agreePrivacy: { section: '약관 동의', label: '개인정보 처리방침' },
};

const FIELD_ORDER = [
  'applicantName',
  'phoneNumber',
  'parentEmail',
  'referralSource',
  'referralOther',
  'childName',
  'childGrade',
  'childPhone',
  'childEmail',
  'username',
  'password',
  'confirmPassword',
  'agreeTerms',
  'agreePrivacy',
];

const getFieldErrorList = (errors) => FIELD_ORDER
  .filter((field) => errors[field])
  .map((field) => ({
    field,
    ...FIELD_META[field],
    message: errors[field],
  }));

const Section = styled.section`
  padding: 96px 0 90px;
  scroll-margin-top: 96px;
  background:
    linear-gradient(rgba(148, 163, 184, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.035) 1px, transparent 1px),
    radial-gradient(circle at 12% 4%, rgba(34, 211, 238, 0.18), transparent 34%),
    radial-gradient(circle at 90% 18%, rgba(129, 140, 248, 0.2), transparent 30%),
    linear-gradient(180deg, #020617 0%, #06111d 48%, #020617 100%);
  background-size: 40px 40px, 40px 40px, auto, auto, auto;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(90deg, transparent, rgba(103, 232, 249, 0.08), transparent),
      radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.06), transparent 34%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 70px 0;
    scroll-margin-top: 78px;
  }
`;

const Header = styled(motion.div)`
  max-width: 940px;
  margin: 0 auto 2rem;
  text-align: center;
  position: relative;
  z-index: 1;
  scroll-margin-top: 112px;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.65rem;
    color: #67e8f9;
    font-size: 0.82rem;
    font-weight: 950;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-bottom: 1.2rem;
  }

  h2 {
    color: #fff;
    font-size: clamp(2.1rem, 4.5vw, 3.8rem);
    font-weight: 950;
    line-height: 1.06;
    letter-spacing: -0.02em;
    margin-bottom: 1.2rem;
  }

  p {
    color: rgba(226, 232, 240, 0.8);
    font-size: 1.08rem;
    line-height: 1.7;
    font-weight: 650;
    margin: 0;
  }
`;

const OfferGrid = styled(motion.div)`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  max-width: 1080px;
  margin: 0 auto 1.5rem;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
`;

const OfferCard = styled(motion.article)`
  --offer-color: ${({ $tone }) => ($tone === 'regular' ? '#67e8f9' : $tone === 'bootcamp' ? '#c4b5fd' : '#6ee7b7')};
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ $tone }) => ($tone === 'regular'
    ? 'rgba(103, 232, 249, 0.28)'
    : $tone === 'bootcamp' ? 'rgba(196, 181, 253, 0.3)' : 'rgba(110, 231, 183, 0.28)')};
  border-radius: 24px;
  background:
    radial-gradient(circle at 100% 0%, ${({ $tone }) => ($tone === 'regular'
      ? 'rgba(34, 211, 238, 0.16)'
      : $tone === 'bootcamp' ? 'rgba(139, 92, 246, 0.18)' : 'rgba(16, 185, 129, 0.16)')}, transparent 42%),
    linear-gradient(145deg, rgba(15, 23, 42, 0.92), rgba(2, 6, 23, 0.84));
  padding: 1.35rem;
  overflow: hidden;
  box-shadow: 0 22px 55px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05);

  .offer-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.8rem;
  }

  .offer-icon {
    width: 42px;
    height: 42px;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid color-mix(in srgb, var(--offer-color) 34%, transparent);
    border-radius: 14px;
    background: color-mix(in srgb, var(--offer-color) 12%, transparent);
    color: var(--offer-color);
    font-size: 1.12rem;
  }

  .offer-kicker {
    color: var(--offer-color);
    font-size: 0.72rem;
    font-weight: 950;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  h3 {
    color: #fff;
    font-size: 1.18rem;
    font-weight: 950;
    line-height: 1.28;
    margin: 0.35rem 0 0;
  }

  .price {
    color: #fff;
    font-size: clamp(1.55rem, 3vw, 2.05rem);
    font-weight: 950;
    letter-spacing: -0.035em;
    line-height: 1.08;
    margin: 1.2rem 0 0.2rem;
    word-break: keep-all;
  }

  .original {
    min-height: 1.15rem;
    color: rgba(226, 232, 240, 0.48);
    font-size: 0.76rem;
    font-weight: 760;
    text-decoration: ${({ $tone }) => ($tone === 'direct' ? 'line-through' : 'none')};
  }

  p {
    color: rgba(226, 232, 240, 0.7);
    font-size: 0.86rem;
    font-weight: 700;
    line-height: 1.6;
    margin: 0.9rem 0 1.15rem;
  }

  .offer-link {
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    margin-top: auto;
    border: 1px solid color-mix(in srgb, var(--offer-color) 32%, transparent);
    border-radius: 13px;
    background: color-mix(in srgb, var(--offer-color) 10%, transparent);
    color: var(--offer-color);
    font-size: 0.8rem;
    font-weight: 950;
    padding: 0.65rem 0.78rem;
    text-decoration: none;
    transition: transform 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  }

  .offer-link:hover,
  .offer-link:focus-visible {
    color: #fff;
    background: color-mix(in srgb, var(--offer-color) 18%, transparent);
    border-color: var(--offer-color);
    transform: translateY(-1px);
    outline: none;
  }

  @media (max-width: 576px) {
    padding: 1.05rem;
    border-radius: 20px;

    .offer-icon {
      width: 38px;
      height: 38px;
      border-radius: 12px;
    }

    .price {
      font-size: 1.55rem;
      margin-top: 0.9rem;
    }

    p {
      margin: 0.7rem 0 0.9rem;
    }
  }
`;

const Panel = styled(motion.div)`
  position: relative;
  z-index: 1;
  background:
    linear-gradient(145deg, rgba(15, 23, 42, 0.9), rgba(2, 6, 23, 0.78));
  border: 1px solid rgba(203, 213, 225, 0.16);
  border-radius: 28px;
  box-shadow: 0 46px 140px rgba(0, 0, 0, 0.44), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(24px);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(90deg, rgba(103, 232, 249, 0.22), transparent 28%, transparent 72%, rgba(167, 139, 250, 0.18));
    height: 1px;
  }
`;

const ColumnHeading = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.35rem;
  position: relative;
  text-align: center;

  .title {
    color: #fff;
    font-size: 1.28rem;
    font-weight: 950;
    letter-spacing: -0.01em;
  }

  .desc {
    color: rgba(226, 232, 240, 0.56);
    font-size: 0.88rem;
    font-weight: 700;
    line-height: 1.55;
    margin-top: 0.35rem;
  }

  .spinner-border {
    position: absolute;
    top: 0.2rem;
    right: 0;
  }

  @media (max-width: 576px) {
    .spinner-border {
      position: static;
      margin-top: 0.2rem;
    }
  }
`;

const ProgramSwitch = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
  margin-bottom: 1rem;
  padding: 0.42rem;
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.48);

  button {
    min-width: 0;
    min-height: 52px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.7rem;
    border: 1px solid rgba(148, 163, 184, 0.08);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.025);
    color: rgba(226, 232, 240, 0.62);
    padding: 0.72rem 0.85rem;
    text-align: left;
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  }

  button[aria-selected='true'] {
    border-color: rgba(103, 232, 249, 0.4);
    background: linear-gradient(135deg, rgba(8, 145, 178, 0.22), rgba(79, 70, 229, 0.16));
    color: #fff;
    box-shadow: 0 10px 26px rgba(8, 145, 178, 0.12);
  }

  button:hover,
  button:focus-visible {
    border-color: rgba(103, 232, 249, 0.42);
    color: #fff;
    transform: translateY(-1px);
    outline: none;
  }

  button:disabled,
  button:disabled:hover,
  button:disabled:focus-visible {
    border-color: rgba(148, 163, 184, 0.08);
    background: rgba(255, 255, 255, 0.018);
    color: rgba(226, 232, 240, 0.38);
    cursor: default;
    transform: none;
    outline: none;
  }

  .switch-label {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    font-size: 0.84rem;
    font-weight: 950;
    line-height: 1.25;
  }

  .switch-label small {
    color: rgba(226, 232, 240, 0.5);
    font-size: 0.68rem;
    font-weight: 720;
  }

  .switch-count {
    flex: 0 0 auto;
    min-width: 1.9rem;
    height: 1.9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.09);
    color: #a5f3fc;
    font-size: 0.75rem;
    font-weight: 950;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ScheduleToolbar = styled.div`
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    radial-gradient(circle at 0% 0%, rgba(34, 211, 238, 0.1), transparent 34%),
    rgba(15, 23, 42, 0.5);
  border-radius: 22px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);

  .toolbar-top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 370px);
    align-items: center;
    gap: 1rem;
  }

  .overview {
    min-width: 0;
    text-align: left;
  }

  .overview strong {
    display: block;
    color: #f8fafc;
    font-size: 1rem;
    font-weight: 950;
    line-height: 1.3;
  }

  .overview span {
    display: block;
    color: rgba(226, 232, 240, 0.58);
    font-size: 0.8rem;
    font-weight: 720;
    line-height: 1.45;
    margin-top: 0.22rem;
  }

  .filter-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.85rem;
    padding-top: 0.85rem;
    border-top: 1px solid rgba(148, 163, 184, 0.1);
  }

  .filter-label {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.38rem;
    color: rgba(226, 232, 240, 0.64);
    font-size: 0.78rem;
    font-weight: 900;
  }

  @media (max-width: 720px) {
    .toolbar-top {
      grid-template-columns: 1fr;
    }

    .filter-row {
      align-items: flex-start;
      flex-direction: column;
      gap: 0.58rem;
    }
  }

  @media (max-width: 576px) {
    padding: 0.85rem;
    border-radius: 18px;
  }
`;

const ScheduleSearch = styled.div`
  position: relative;
  min-width: 0;

  > svg {
    position: absolute;
    top: 50%;
    left: 0.9rem;
    width: 0.95rem;
    height: 0.95rem;
    color: rgba(103, 232, 249, 0.78);
    transform: translateY(-50%);
    pointer-events: none;
  }

  input {
    width: 100%;
    min-height: 46px;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 15px;
    background: rgba(2, 6, 23, 0.54);
    color: #f8fafc;
    font-size: 0.86rem;
    font-weight: 760;
    padding: 0.72rem 2.7rem 0.72rem 2.55rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }

  input::placeholder {
    color: rgba(148, 163, 184, 0.58);
    font-weight: 650;
  }

  input:focus {
    border-color: #67e8f9;
    background: rgba(8, 47, 73, 0.28);
    box-shadow: 0 0 0 3px rgba(103, 232, 249, 0.13);
    outline: none;
  }

  button {
    position: absolute;
    top: 50%;
    right: 0.55rem;
    width: 34px;
    height: 34px;
    border-radius: 11px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(226, 232, 240, 0.62);
    transform: translateY(-50%);
    transition: color 0.2s ease, background 0.2s ease;
  }

  button:hover,
  button:focus-visible {
    color: #fff;
    background: rgba(148, 163, 184, 0.14);
    outline: none;
  }
`;

const ScheduleFilters = styled.div`
  min-width: 0;
  display: flex;
  gap: 0.48rem;
  padding: 0.3rem 0.15rem 0.55rem;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 720px) {
    width: 100%;
  }
`;

const ScheduleFilterButton = styled.button`
  flex: 0 0 auto;
  min-height: 38px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(103, 232, 249, 0.72)' : 'rgba(148, 163, 184, 0.15)')};
  border-radius: 999px;
  background: ${({ $active }) => ($active
    ? 'linear-gradient(135deg, rgba(8, 145, 178, 0.34), rgba(37, 99, 235, 0.2))'
    : 'rgba(255, 255, 255, 0.035)')};
  color: ${({ $active }) => ($active ? '#ecfeff' : 'rgba(226, 232, 240, 0.68)')};
  font-size: 0.78rem;
  font-weight: 900;
  line-height: 1;
  padding: 0.58rem 0.78rem;
  box-shadow: ${({ $active }) => ($active ? '0 8px 22px rgba(8, 145, 178, 0.16)' : 'none')};
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  span {
    display: inline-flex;
    min-width: 1.35rem;
    height: 1.35rem;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    margin-left: 0.32rem;
    background: ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.14)' : 'rgba(148, 163, 184, 0.1)')};
    font-size: 0.68rem;
  }

  &:hover,
  &:focus-visible {
    color: #fff;
    border-color: rgba(103, 232, 249, 0.58);
    transform: translateY(-1px);
    outline: none;
  }
`;

const ScheduleResultMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  margin: 0.2rem 0 0.7rem;
  padding: 0 0.18rem;
  color: rgba(226, 232, 240, 0.56);
  font-size: 0.78rem;
  font-weight: 760;
  line-height: 1.4;

  strong {
    color: #a5f3fc;
    font-weight: 950;
  }
`;

const EventList = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const ScheduleCollection = styled.section`
  scroll-margin-top: 110px;
  border: 1px solid ${({ $tone }) => ($tone === 'paid' ? 'rgba(196, 181, 253, 0.24)' : 'rgba(110, 231, 183, 0.22)')};
  border-radius: 24px;
  background: ${({ $tone }) => ($tone === 'paid'
    ? 'linear-gradient(145deg, rgba(76, 29, 149, 0.12), rgba(15, 23, 42, 0.32))'
    : 'linear-gradient(145deg, rgba(6, 78, 59, 0.12), rgba(15, 23, 42, 0.32))')};
  padding: 1rem;
  margin-top: 1rem;

  .collection-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.9rem;
  }

  .collection-kicker {
    color: ${({ $tone }) => ($tone === 'paid' ? '#c4b5fd' : '#6ee7b7')};
    font-size: 0.7rem;
    font-weight: 950;
    letter-spacing: 0.11em;
    text-transform: uppercase;
  }

  h3 {
    color: #fff;
    font-size: 1.08rem;
    font-weight: 950;
    line-height: 1.3;
    margin: 0.28rem 0 0;
  }

  .collection-description {
    color: rgba(226, 232, 240, 0.58);
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.5;
    margin: 0.28rem 0 0;
  }

  .collection-price {
    flex: 0 0 auto;
    border: 1px solid ${({ $tone }) => ($tone === 'paid' ? 'rgba(196, 181, 253, 0.3)' : 'rgba(110, 231, 183, 0.3)')};
    border-radius: 999px;
    background: ${({ $tone }) => ($tone === 'paid' ? 'rgba(124, 58, 237, 0.13)' : 'rgba(5, 150, 105, 0.13)')};
    color: ${({ $tone }) => ($tone === 'paid' ? '#ddd6fe' : '#a7f3d0')};
    font-size: 0.76rem;
    font-weight: 950;
    padding: 0.45rem 0.68rem;
  }

  @media (max-width: 576px) {
    padding: 0.75rem;
    border-radius: 19px;

    .collection-head {
      flex-direction: column;
      gap: 0.58rem;
    }
  }
`;

const EventGroup = styled(motion.article)`
  position: relative;
  display: grid;
  grid-template-columns: 128px minmax(0, 1fr);
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.5)),
    radial-gradient(circle at 100% 0%, rgba(129, 140, 248, 0.08), transparent 35%);
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.035);
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    border-color: rgba(103, 232, 249, 0.26);
    transform: translateY(-1px);
    box-shadow: 0 18px 44px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.045);
  }

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const EventDateBlock = styled.div`
  min-height: 148px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(148, 163, 184, 0.1);
  background:
    radial-gradient(circle at 50% 12%, rgba(103, 232, 249, 0.15), transparent 38%),
    rgba(8, 47, 73, 0.18);
  padding: 1rem 0.75rem;
  text-align: center;

  .month {
    color: #67e8f9;
    font-size: 0.72rem;
    font-weight: 950;
    letter-spacing: 0.08em;
  }

  .day {
    color: #fff;
    font-size: 2.15rem;
    font-weight: 950;
    line-height: 1;
    letter-spacing: -0.04em;
    margin: 0.26rem 0 0.18rem;
  }

  .weekday {
    color: rgba(226, 232, 240, 0.65);
    font-size: 0.76rem;
    font-weight: 850;
  }

  .dday {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 24px;
    border: 1px solid rgba(74, 222, 128, 0.22);
    border-radius: 999px;
    background: rgba(34, 197, 94, 0.11);
    color: #bbf7d0;
    font-size: 0.66rem;
    font-weight: 950;
    padding: 0.28rem 0.58rem;
    margin-top: 0.55rem;
  }

  @media (max-width: 680px) {
    min-height: auto;
    flex-direction: row;
    justify-content: flex-start;
    gap: 0.5rem;
    border-right: 0;
    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
    padding: 0.72rem 0.9rem;
    text-align: left;

    .day {
      order: -1;
      font-size: 1.42rem;
      margin: 0;
    }

    .month,
    .weekday {
      font-size: 0.72rem;
    }

    .dday {
      margin: 0 0 0 auto;
    }
  }
`;

const EventGroupBody = styled.div`
  min-width: 0;
  padding: 1rem;

  .group-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.8rem;
    margin-bottom: 0.8rem;
  }

  .group-title-wrap {
    min-width: 0;
    text-align: left;
  }

  .group-title {
    color: #fff;
    font-size: 1rem;
    font-weight: 950;
    line-height: 1.38;
    letter-spacing: -0.012em;
    word-break: keep-all;
    overflow-wrap: anywhere;
  }

  .group-hint {
    color: rgba(226, 232, 240, 0.5);
    font-size: 0.74rem;
    font-weight: 720;
    line-height: 1.45;
    margin-top: 0.24rem;
  }

  .group-location {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: #cbd5e1;
    font-size: 0.76rem;
    font-weight: 850;
    line-height: 1.45;
    margin-top: 0.38rem;
  }

  .group-location svg {
    flex: 0 0 auto;
    color: #a5b4fc;
  }

  @media (max-width: 520px) {
    padding: 0.85rem;

    .group-head {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
`;

const EventCategory = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.32rem;
  min-height: 28px;
  border: 1px solid rgba(103, 232, 249, 0.2);
  border-radius: 999px;
  background: rgba(8, 145, 178, 0.1);
  color: #a5f3fc;
  font-size: 0.7rem;
  font-weight: 950;
  padding: 0.35rem 0.62rem;

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const EventSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.58rem;

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

const EventButton = styled(motion.button)`
  min-width: 0;
  min-height: 62px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.68rem;
  border: 1px solid ${({ $active, $full }) => {
    if ($active) return 'rgba(103, 232, 249, 0.8)';
    return $full ? 'rgba(248, 113, 113, 0.14)' : 'rgba(148, 163, 184, 0.15)';
  }};
  border-radius: 16px;
  background: ${({ $active, $full }) => {
    if ($active) return 'linear-gradient(135deg, rgba(8, 145, 178, 0.3), rgba(37, 99, 235, 0.16))';
    return $full ? 'rgba(127, 29, 29, 0.08)' : 'rgba(255, 255, 255, 0.035)';
  }};
  color: #fff;
  padding: 0.68rem 0.75rem;
  text-align: left;
  box-shadow: ${({ $active }) => ($active ? '0 10px 28px rgba(8, 145, 178, 0.16)' : 'none')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.58 : 1)};
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  .slot-icon {
    width: 35px;
    height: 35px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${({ $full }) => ($full ? 'rgba(248, 113, 113, 0.18)' : 'rgba(103, 232, 249, 0.16)')};
    border-radius: 12px;
    background: ${({ $full }) => ($full ? 'rgba(248, 113, 113, 0.08)' : 'rgba(8, 145, 178, 0.09)')};
    color: ${({ $full }) => ($full ? '#fca5a5' : '#67e8f9')};
  }

  .slot-copy {
    min-width: 0;
  }

  .slot-time {
    display: block;
    color: ${({ $full }) => ($full ? 'rgba(254, 226, 226, 0.74)' : '#f8fafc')};
    font-size: 0.88rem;
    font-weight: 950;
    line-height: 1.28;
    white-space: nowrap;
  }

  .slot-state {
    display: block;
    color: ${({ $full }) => ($full ? '#fca5a5' : 'rgba(226, 232, 240, 0.52)')};
    font-size: 0.69rem;
    font-weight: 820;
    line-height: 1.3;
    margin-top: 0.18rem;
  }

  .slot-arrow {
    color: ${({ $full }) => ($full ? 'rgba(248, 113, 113, 0.46)' : 'rgba(103, 232, 249, 0.76)')};
    transition: transform 0.2s ease;
  }

  &:hover,
  &:focus-visible {
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
    border-color: ${({ disabled }) => (disabled ? 'rgba(248, 113, 113, 0.14)' : '#67e8f9')};
    background: ${({ disabled }) => (disabled ? 'rgba(127, 29, 29, 0.08)' : 'rgba(8, 145, 178, 0.14)')};
    box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 12px 28px rgba(8, 145, 178, 0.14)')};
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(103, 232, 249, 0.16);
  }

  &:hover .slot-arrow {
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateX(2px)')};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover,
    &:focus-visible,
    &:hover .slot-arrow {
      transform: none;
    }
  }
`;

const ScheduleSkeleton = styled.div`
  display: grid;
  gap: 0.85rem;

  .skeleton-row {
    height: 148px;
    border: 1px solid rgba(148, 163, 184, 0.1);
    border-radius: 22px;
    background:
      linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.055), transparent),
      rgba(255, 255, 255, 0.025);
    background-size: 220% 100%, auto;
    animation: schedule-shimmer 1.5s ease-in-out infinite;
  }

  @keyframes schedule-shimmer {
    from { background-position: 180% 0, 0 0; }
    to { background-position: -40% 0, 0 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .skeleton-row {
      animation: none;
    }
  }
`;

const StateAction = styled.button`
  min-height: 42px;
  max-width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.42rem;
  border: 1px solid rgba(103, 232, 249, 0.3);
  border-radius: 13px;
  background: rgba(8, 145, 178, 0.12);
  color: #a5f3fc;
  font-size: 0.8rem;
  font-weight: 900;
  line-height: 1.5;
  text-decoration: none;
  padding: 0.62rem 0.85rem;
  margin-top: 0.9rem;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover,
  &:focus-visible {
    background: rgba(8, 145, 178, 0.2);
    border-color: #67e8f9;
    transform: translateY(-1px);
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid #397bcf;
    outline-offset: 3px;
  }
`;

const OtConsultationLink = () => (
  <StateAction
    as="a"
    href="https://pf.kakao.com/_DZhmn"
    target="_blank"
    rel="noopener noreferrer"
  >
    무료 OT 일정 상담하기 <FiExternalLink aria-hidden="true" />
  </StateAction>
);

const ParentConsultationNotice = styled.aside`
  margin: 0 0 1.25rem;
  padding: 1rem 1.1rem;
  border: 1px solid #d8e5f3;
  border-radius: 16px;
  background: #f3f7fc;
  color: #243b55;
  line-height: 1.65;
  word-break: keep-all;

  strong {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.9rem;
  }

  p {
    margin: 0.35rem 0 0;
    color: #526579;
    font-size: 0.85rem;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin-top: 0.6rem;
    color: #185aa4;
    font-size: 0.85rem;
    font-weight: 750;
    text-underline-offset: 3px;
  }

  a:hover {
    color: #103e73;
  }

  a:focus-visible {
    outline: 3px solid #397bcf;
    outline-offset: 4px;
    border-radius: 3px;
  }
`;

const ParentConsultation = () => (
  <ParentConsultationNotice aria-label="학부모 전화 상담 안내">
    <strong><FiPhone aria-hidden="true" /> 학부모 상담 안내</strong>
    <p>나니아랩 카카오톡으로 문의해 주세요. 매니저와 시간을 조율한 뒤 학부모 전화 상담을 진행합니다.</p>
    <a href="https://pf.kakao.com/_DZhmn" target="_blank" rel="noopener noreferrer">
      카카오톡으로 학부모 상담 문의 <FiExternalLink aria-hidden="true" />
    </a>
  </ParentConsultationNotice>
);

const ListShell = styled.div`
  padding: 2rem;

  @media (max-width: 576px) {
    padding: 1.25rem;
  }
`;

const FieldLabel = styled(Form.Label)`
  color: rgba(226, 232, 240, 0.78);
  font-size: 0.82rem;
  font-weight: 900;
  margin-bottom: 0.48rem;
  display: inline-flex;
  align-items: center;
  gap: 0.36rem;

  svg {
    color: rgba(103, 232, 249, 0.82);
  }
`;

const FieldControl = styled(Form.Control)`
  background: rgba(15, 23, 42, 0.76) !important;
  border: 1px solid ${({ $hasError }) => ($hasError ? 'rgba(248, 113, 113, 0.9)' : 'rgba(148, 163, 184, 0.2)')} !important;
  border-radius: 14px !important;
  color: #fff !important;
  font-weight: 800;
  padding: 0.82rem 0.95rem !important;
  box-shadow: ${({ $hasError }) => ($hasError
    ? '0 0 0 0.16rem rgba(248, 113, 113, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.04)'
    : 'inset 0 1px 0 rgba(255, 255, 255, 0.04)')};

  &::placeholder {
    color: rgba(100, 116, 139, 0.58);
    font-weight: 520;
    opacity: 0.76;
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(103, 232, 249, 0.16) !important;
    border-color: ${({ $hasError }) => ($hasError ? '#f87171' : '#67e8f9')} !important;
  }
`;

const FormPanel = styled.section`
  border: 1px solid rgba(148, 163, 184, 0.15);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.38)),
    radial-gradient(circle at 0% 0%, rgba(103, 232, 249, 0.08), transparent 34%);
  border-radius: 18px;
  padding: 0.95rem;
  margin-bottom: 0.85rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.045);
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.78rem 0.82rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled(Form.Group)`
  min-width: 0;
  margin: 0;
  ${({ $full }) => ($full ? 'grid-column: 1 / -1;' : '')}
`;

const FieldErrorText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.38rem;
  color: #fecaca;
  font-size: 0.76rem;
  font-weight: 850;
  line-height: 1.42;
  margin-top: 0.42rem;

  svg {
    flex: 0 0 auto;
    margin-top: 0.12rem;
  }
`;

const FormErrorSummary = styled(motion.div)`
  border: 1px solid rgba(248, 113, 113, 0.34);
  background:
    radial-gradient(circle at 0% 0%, rgba(248, 113, 113, 0.14), transparent 34%),
    rgba(127, 29, 29, 0.18);
  border-radius: 18px;
  padding: 0.95rem;
  margin-bottom: 0.9rem;
  color: #fee2e2;
  box-shadow: 0 14px 30px rgba(127, 29, 29, 0.16);

  .summary-head {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    margin-bottom: 0.75rem;
  }

  .summary-icon {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fecaca;
    background: rgba(248, 113, 113, 0.13);
    border: 1px solid rgba(248, 113, 113, 0.22);
  }

  strong {
    display: block;
    color: #fff;
    font-size: 0.94rem;
    font-weight: 950;
    line-height: 1.25;
    margin-bottom: 0.18rem;
  }

  p {
    margin: 0;
    color: rgba(254, 226, 226, 0.78);
    font-size: 0.8rem;
    font-weight: 760;
    line-height: 1.45;
  }

  .error-list {
    display: grid;
    gap: 0.42rem;
  }

  button {
    width: 100%;
    border: 1px solid rgba(254, 202, 202, 0.16);
    border-radius: 13px;
    background: rgba(255, 255, 255, 0.045);
    color: #fee2e2;
    padding: 0.6rem 0.68rem;
    text-align: left;
    display: grid;
    grid-template-columns: minmax(118px, 0.42fr) minmax(0, 1fr);
    gap: 0.55rem;
    align-items: center;
    font-size: 0.8rem;
    font-weight: 800;
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  }

  button:hover,
  button:focus-visible {
    background: rgba(248, 113, 113, 0.1);
    border-color: rgba(254, 202, 202, 0.42);
    transform: translateY(-1px);
    outline: none;
  }

  .error-place {
    color: #fff;
    font-weight: 950;
    white-space: nowrap;
  }

  .error-message {
    min-width: 0;
    color: rgba(254, 226, 226, 0.82);
    overflow-wrap: anywhere;
  }

  @media (max-width: 576px) {
    button {
      grid-template-columns: 1fr;
      gap: 0.2rem;
    }
  }
`;

const MiniNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  color: rgba(226, 232, 240, 0.7);
  border: 1px solid rgba(103, 232, 249, 0.18);
  background: linear-gradient(135deg, rgba(8, 145, 178, 0.12), rgba(15, 23, 42, 0.6));
  border-radius: 16px;
  padding: 1rem;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.6;

  svg {
    flex: 0 0 auto;
    margin-top: 0.18rem;
    color: #67e8f9;
  }
`;

const EventDetailBox = styled.div`
  margin-top: 0.8rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(103, 232, 249, 0.16);
  background: rgba(8, 47, 73, 0.22);
  border-radius: 14px;
  padding: 0.85rem 0.9rem;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: keep-all;

  .detail-title {
    display: inline-flex;
    align-items: center;
    gap: 0.42rem;
    color: #e0f2fe;
    font-size: 0.8rem;
    font-weight: 950;
    margin-bottom: 0.45rem;
  }

  .detail-title svg {
    color: #67e8f9;
  }

  .detail-body {
    color: rgba(224, 242, 254, 0.86);
    font-size: 0.84rem;
    font-weight: 700;
    line-height: 1.65;
    white-space: pre-wrap;
  }

  a {
    display: inline;
    color: #67e8f9;
    text-decoration: none;
    font-weight: 950;
    word-break: break-all;
    overflow-wrap: anywhere;
  }

  a svg {
    display: inline;
    margin-left: 0.28rem;
    vertical-align: -0.12em;
  }

  a:hover,
  a:focus-visible {
    color: #bfdbfe;
    outline: none;
  }
`;

const MyPageNote = styled.div`
  margin-top: 1.5rem;
  border: 1px solid rgba(103, 232, 249, 0.18);
  background: rgba(8, 47, 73, 0.16);
  border-radius: 16px;
  padding: 0.95rem 1rem;
  color: rgba(226, 232, 240, 0.72);
  font-size: 0.86rem;
  font-weight: 760;
  line-height: 1.6;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: #67e8f9;
    font-weight: 950;
    text-decoration: none;
  }

  a:hover,
  a:focus-visible {
    color: #bae6fd;
    outline: none;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const FormSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.58rem;
  color: #fff;
  font-size: 0.96rem;
  font-weight: 950;
  margin: 0 0 0.82rem;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #67e8f9;
    box-shadow: 0 0 18px rgba(103, 232, 249, 0.85);
  }
`;

const AccountGuidance = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: -0.1rem 0 0.82rem;
  padding: 0.8rem 0.9rem;
  border-radius: 16px;
  border: 1px solid rgba(125, 211, 252, 0.18);
  background:
    linear-gradient(135deg, rgba(14, 116, 144, 0.16), rgba(15, 23, 42, 0.22));
  color: rgba(226, 232, 240, 0.74);
  font-size: 0.84rem;
  font-weight: 760;
  line-height: 1.58;
  text-align: left;
  word-break: keep-all;
  overflow-wrap: anywhere;

  svg {
    flex: 0 0 auto;
    width: 0.92rem;
    height: 0.92rem;
    margin-top: 0.28rem;
    color: #a7f3d0;
  }

  span {
    min-width: 0;
  }
`;

const SelectionPrompt = styled(motion.div)`
  border: 1px dashed rgba(103, 232, 249, 0.28);
  background:
    radial-gradient(circle at top left, rgba(103, 232, 249, 0.12), transparent 38%),
    rgba(15, 23, 42, 0.46);
  border-radius: 22px;
  padding: 2rem 1.25rem;
  text-align: center;
  color: rgba(226, 232, 240, 0.72);
  font-size: 0.92rem;
  font-weight: 760;
  line-height: 1.65;

  .icon {
    width: 46px;
    height: 46px;
    margin: 0 auto 1rem;
    border-radius: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #67e8f9;
    background: rgba(8, 145, 178, 0.15);
    border: 1px solid rgba(103, 232, 249, 0.2);
  }

  strong {
    display: block;
    color: #fff;
    font-size: 1.05rem;
    font-weight: 950;
    margin-bottom: 0.35rem;
  }
`;

const ModalEventSummary = styled.div`
  border: 1px solid rgba(103, 232, 249, 0.22);
  background:
    radial-gradient(circle at top left, rgba(103, 232, 249, 0.14), transparent 42%),
    rgba(8, 47, 73, 0.18);
  border-radius: 18px;
  padding: 1rem;
  margin-bottom: 0.9rem;

  .label {
    color: #67e8f9;
    font-size: 0.72rem;
    font-weight: 950;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 0.42rem;
  }

  .title {
    color: #fff;
    font-size: 1rem;
    font-weight: 950;
    line-height: 1.35;
    margin-bottom: 0.62rem;
    word-break: keep-all;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
    color: rgba(226, 232, 240, 0.72);
    font-size: 0.82rem;
    font-weight: 760;
  }

  .meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
`;

const PrimaryActionButton = styled.button`
  width: 100%;
  border: 0;
  border-radius: 18px;
  background: linear-gradient(135deg, #06b6d4, #2563eb 54%, #7c3aed);
  color: #fff;
  font-size: 1rem;
  font-weight: 950;
  padding: 1rem 1.1rem;
  box-shadow: 0 18px 44px rgba(37, 99, 235, 0.28);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-1px);
    box-shadow: 0 22px 56px rgba(6, 182, 212, 0.3);
    filter: brightness(1.04);
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
    transform: none;
    filter: none;
  }
`;

const PublicApplyModal = styled(Modal)`
  .modal-dialog {
    max-width: 760px;
  }

  .modal-content {
    background: #020617;
    color: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 24px;
    box-shadow: 0 42px 130px rgba(0, 0, 0, 0.72);
    overflow: hidden;
  }

  .modal-header {
    border: 0;
    padding: 1.45rem 1.65rem 0.75rem;
    background:
      radial-gradient(circle at 18% 0%, rgba(103, 232, 249, 0.16), transparent 32%),
      linear-gradient(180deg, rgba(15, 23, 42, 0.94), rgba(2, 6, 23, 0.42));
  }

  .modal-title {
    width: 100%;
  }

  .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%);
    opacity: 0.8;
  }

  .modal-body {
    padding: 1rem 1.65rem 1.65rem;
    max-height: min(76vh, 780px);
    overflow-y: auto;
  }

  @media (max-width: 576px) {
    .modal-dialog {
      margin: 0.65rem;
    }

    .modal-content {
      border-radius: 20px;
    }

    .modal-header,
    .modal-body {
      padding-left: 1rem;
      padding-right: 1rem;
    }
  }
`;

const ModalTitleBlock = styled.div`
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #67e8f9;
    font-size: 0.72rem;
    font-weight: 950;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 0.45rem;
  }

  h3 {
    color: #fff;
    font-size: clamp(1.2rem, 3vw, 1.65rem);
    font-weight: 950;
    line-height: 1.35;
    margin: 0;
    word-break: keep-all;
  }

  p {
    color: rgba(226, 232, 240, 0.6);
    font-size: 0.88rem;
    font-weight: 720;
    line-height: 1.55;
    margin: 0.45rem 0 0;
  }
`;

const ReferralSelectShell = styled.div`
  position: relative;
  z-index: ${({ $open }) => ($open ? 8 : 1)};
`;

const ReferralTrigger = styled.button`
  width: 100%;
  min-height: 50px;
  border: 1px solid ${({ $hasError, $open }) => {
    if ($hasError) return 'rgba(248, 113, 113, 0.9)';
    return $open ? 'rgba(103, 232, 249, 0.82)' : 'rgba(148, 163, 184, 0.2)';
  }};
  border-radius: 14px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.64)),
    radial-gradient(circle at 0% 0%, rgba(103, 232, 249, 0.08), transparent 34%);
  color: ${({ $empty }) => ($empty ? 'rgba(148, 163, 184, 0.64)' : '#fff')};
  font-weight: ${({ $empty }) => ($empty ? 680 : 850)};
  padding: 0.82rem 0.9rem 0.82rem 0.95rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.65rem;
  text-align: left;
  box-shadow: ${({ $hasError, $open }) => {
    if ($hasError) return '0 0 0 0.16rem rgba(248, 113, 113, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.04)';
    return $open
      ? '0 0 0 0.2rem rgba(103, 232, 249, 0.16), 0 18px 42px rgba(8, 145, 178, 0.16)'
      : 'inset 0 1px 0 rgba(255, 255, 255, 0.04)';
  }};
  transition: border-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover,
  &:focus-visible {
    border-color: ${({ $hasError }) => ($hasError ? '#f87171' : '#67e8f9')};
    outline: none;
  }

  &:hover {
    transform: translateY(-1px);
  }

  .select-value {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    color: #67e8f9;
    transition: transform 0.2s ease;
    transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

const ReferralMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.48rem);
  left: 0;
  right: 0;
  display: grid;
  gap: 0.28rem;
  padding: 0.42rem;
  border: 1px solid rgba(103, 232, 249, 0.22);
  border-radius: 16px;
  background:
    linear-gradient(145deg, rgba(15, 23, 42, 0.98), rgba(2, 6, 23, 0.96)),
    radial-gradient(circle at 0% 0%, rgba(103, 232, 249, 0.12), transparent 36%);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(18px);
  max-height: 260px;
  overflow-y: auto;
`;

const ReferralOption = styled.button`
  width: 100%;
  border: 1px solid ${({ $selected }) => ($selected ? 'rgba(103, 232, 249, 0.36)' : 'transparent')};
  border-radius: 12px;
  background: ${({ $selected }) => ($selected ? 'rgba(8, 145, 178, 0.2)' : 'rgba(255, 255, 255, 0.015)')};
  color: ${({ $selected }) => ($selected ? '#fff' : 'rgba(226, 232, 240, 0.82)')};
  font-size: 0.88rem;
  font-weight: 850;
  padding: 0.72rem 0.76rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.65rem;
  text-align: left;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;

  &:hover,
  &:focus-visible {
    background: rgba(8, 145, 178, 0.18);
    border-color: rgba(103, 232, 249, 0.28);
    color: #fff;
    outline: none;
    transform: translateY(-1px);
  }

  svg {
    color: #67e8f9;
    opacity: ${({ $selected }) => ($selected ? 1 : 0)};
  }
`;

const ConsentPanel = styled.div`
  display: grid;
  gap: 0;

  .form-check-label {
    color: rgba(226, 232, 240, 0.82);
    font-weight: 800;
    font-size: 0.9rem;
    line-height: 1.45;
  }

  .form-check {
    min-width: 0;
    margin: 0;
    padding-top: 0.1rem;
  }

  .form-check-input {
    margin-top: 0.22rem;
  }
`;

const ConsentRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.8rem;
  min-width: 0;
  padding: 0.72rem 0.1rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);

  &:first-child {
    padding-top: 0.1rem;
  }

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0.1rem;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    align-items: flex-start;
    gap: 0.55rem;
  }
`;

const InlineLinkButton = styled.button`
  justify-self: end;
  border: 1px solid rgba(103, 232, 249, 0.24);
  border-radius: 999px;
  background: rgba(8, 145, 178, 0.12);
  color: #67e8f9;
  font-size: 0.82rem;
  font-weight: 950;
  padding: 0.45rem 0.72rem;
  white-space: nowrap;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: #bae6fd;
    background: rgba(8, 145, 178, 0.2);
    border-color: rgba(103, 232, 249, 0.5);
    outline: none;
  }

  @media (max-width: 576px) {
    justify-self: start;
  }
`;

const CompletionPanel = styled(motion.div)`
  position: relative;
  border: 1px solid rgba(45, 212, 191, 0.28);
  background:
    radial-gradient(circle at 0% 0%, rgba(45, 212, 191, 0.22), transparent 34%),
    linear-gradient(135deg, rgba(15, 118, 110, 0.24), rgba(15, 23, 42, 0.78));
  border-radius: 22px;
  padding: 1.1rem;
  color: #f8fafc;
  box-shadow:
    0 20px 58px rgba(0, 0, 0, 0.26),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: auto 1.1rem 4.7rem;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(125, 211, 252, 0.34), transparent);
  }

  .dismiss-button {
    position: absolute;
    top: 0.85rem;
    right: 0.85rem;
    z-index: 1;
    width: 34px;
    height: 34px;
    border: 1px solid rgba(226, 232, 240, 0.16);
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.42);
    color: rgba(226, 232, 240, 0.74);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
  }

  .dismiss-button:hover,
  .dismiss-button:focus-visible {
    color: #fff;
    background: rgba(15, 23, 42, 0.7);
    border-color: rgba(125, 211, 252, 0.34);
    outline: none;
  }

  .completion-head {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.9rem;
    padding-right: 2.45rem;
  }

  .completion-icon {
    width: 48px;
    height: 48px;
    border-radius: 16px;
    background:
      linear-gradient(135deg, rgba(34, 197, 94, 0.28), rgba(20, 184, 166, 0.16));
    border: 1px solid rgba(134, 239, 172, 0.26);
    color: #86efac;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 12px 28px rgba(20, 184, 166, 0.18);
  }

  .completion-icon svg {
    width: 1.35rem;
    height: 1.35rem;
  }

  .completion-kicker {
    color: #67e8f9;
    font-size: 0.72rem;
    font-weight: 950;
    line-height: 1;
    text-transform: uppercase;
    margin-bottom: 0.34rem;
  }

  h4 {
    color: #fff;
    font-size: clamp(1.08rem, 3vw, 1.28rem);
    font-weight: 950;
    line-height: 1.32;
    margin: 0 0 0.36rem;
    word-break: keep-all;
  }

  p {
    color: rgba(226, 232, 240, 0.74);
    font-size: 0.88rem;
    font-weight: 730;
    line-height: 1.62;
    margin: 0;
    word-break: keep-all;
  }

  .completion-grid {
    display: grid;
    grid-template-columns: minmax(0, 0.82fr) minmax(0, 1.18fr);
    gap: 0.7rem;
    margin-top: 1rem;
  }

  .completion-item {
    min-width: 0;
    border: 1px solid rgba(226, 232, 240, 0.12);
    background: rgba(2, 6, 23, 0.28);
    border-radius: 16px;
    padding: 0.85rem 0.9rem;
  }

  .completion-item span,
  .completion-item small {
    display: block;
    color: rgba(186, 230, 253, 0.68);
    font-size: 0.72rem;
    font-weight: 900;
    line-height: 1.35;
  }

  .completion-item strong {
    display: block;
    color: #fff;
    font-size: 0.98rem;
    font-weight: 950;
    line-height: 1.42;
    margin-top: 0.28rem;
    overflow-wrap: anywhere;
    word-break: keep-all;
  }

  .completion-item small {
    color: rgba(226, 232, 240, 0.62);
    margin-top: 0.22rem;
  }

  .completion-action {
    position: relative;
    z-index: 1;
    width: 100%;
    border: 1px solid rgba(125, 211, 252, 0.26);
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(6, 182, 212, 0.92), rgba(37, 99, 235, 0.95));
    color: #fff;
    text-decoration: none;
    font-size: 0.92rem;
    font-weight: 950;
    padding: 0.9rem 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    box-shadow: 0 16px 34px rgba(37, 99, 235, 0.26);
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  }

  .completion-action:hover,
  .completion-action:focus-visible {
    color: #fff;
    text-decoration: none;
    transform: translateY(-1px);
    filter: brightness(1.04);
    box-shadow: 0 20px 44px rgba(6, 182, 212, 0.28);
    outline: none;
  }

  .completion-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
    margin-top: 0.95rem;
  }

  .completion-actions.single {
    grid-template-columns: 1fr;
  }

  .completion-action.secondary {
    background: rgba(15, 23, 42, 0.78);
    border-color: rgba(103, 232, 249, 0.24);
    box-shadow: none;
  }

  @media (max-width: 576px) {
    padding: 1rem;
    border-radius: 18px;

    &::after {
      display: none;
    }

    .completion-head {
      grid-template-columns: 1fr;
      gap: 0.75rem;
      padding-right: 2.35rem;
    }

    .completion-icon {
      width: 42px;
      height: 42px;
      border-radius: 14px;
    }

    .completion-grid {
      grid-template-columns: 1fr;
    }

    .completion-actions {
      grid-template-columns: 1fr;
    }
  }
`;

const FlowStrip = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.7), rgba(2, 6, 23, 0.44)),
    radial-gradient(circle at 18% 0%, rgba(103, 232, 249, 0.12), transparent 28%);
  border-radius: 18px;
  padding: 0.42rem;
  margin-bottom: 1.5rem;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 12px 28px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  @media (max-width: 576px) {
    border-radius: 16px;
    padding: 0.34rem;
  }
`;

const FlowStep = styled.div`
  position: relative;
  min-width: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.48rem;
  border-radius: 14px;
  padding: 0.55rem 0.5rem;
  color: rgba(226, 232, 240, 0.78);
  font-size: 0.8rem;
  font-weight: 850;
  line-height: 1.15;
  white-space: nowrap;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    width: 1px;
    height: 20px;
    background: linear-gradient(180deg, transparent, rgba(148, 163, 184, 0.24), transparent);
    transform: translateY(-50%);
  }

  &:last-child {
    color: #f8fafc;
  }

  svg {
    width: 0.9rem;
    height: 0.9rem;
  }

  .step-index {
    flex: 0 0 auto;
    width: 25px;
    height: 25px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(103, 232, 249, 0.18);
    background: rgba(103, 232, 249, 0.09);
    border-radius: 999px;
    color: #67e8f9;
    font-size: 0.67rem;
    font-weight: 950;
    letter-spacing: 0;
  }

  .step-label {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.24rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:last-child .step-index {
    border-color: rgba(45, 212, 191, 0.32);
    background: linear-gradient(135deg, rgba(20, 184, 166, 0.96), rgba(14, 165, 233, 0.82));
    color: #f8fafc;
    box-shadow: 0 8px 18px rgba(14, 165, 233, 0.22);
  }

  @media (max-width: 576px) {
    min-height: 42px;
    flex-direction: column;
    gap: 0.24rem;
    padding: 0.45rem 0.12rem;
    font-size: 0.68rem;

    .step-index {
      width: 22px;
      height: 22px;
      font-size: 0.62rem;
    }

    .step-label {
      max-width: 100%;
    }
  }
`;

const EmptyState = styled.div`
  border: 1px dashed rgba(148, 163, 184, 0.26);
  background:
    radial-gradient(circle at 50% 0%, rgba(103, 232, 249, 0.08), transparent 42%),
    rgba(255, 255, 255, 0.035);
  color: rgba(226, 232, 240, 0.72);
  border-radius: 22px;
  padding: 2rem;
  text-align: center;
  font-weight: 750;

  .state-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0 0.75rem;
  }

  .state-icon {
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(103, 232, 249, 0.18);
    border-radius: 15px;
    background: rgba(8, 145, 178, 0.1);
    color: #67e8f9;
    margin-bottom: 0.75rem;
  }

  strong {
    display: block;
    color: #f8fafc;
    font-size: 0.98rem;
    font-weight: 950;
    line-height: 1.4;
  }

  p {
    max-width: 480px;
    color: rgba(226, 232, 240, 0.6);
    font-size: 0.82rem;
    font-weight: 720;
    line-height: 1.55;
    margin: 0.34rem auto 0;
  }
`;

const PremiumApplySection = styled(Section)`
  padding: 124px 0;
  scroll-margin-top: 0;
  background:
    radial-gradient(circle at 8% 5%, rgba(92, 157, 225, 0.11), transparent 29%),
    radial-gradient(circle at 92% 23%, rgba(244, 199, 159, 0.12), transparent 26%),
    linear-gradient(180deg, #f7f7f5 0%, #f2f4f7 47%, #f7f6f2 100%);
  background-size: auto;

  &::before {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.52), transparent 20%, transparent 82%, rgba(255, 255, 255, 0.38));
  }

  ${Header} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, 410px);
    grid-template-rows: auto auto;
    max-width: 1180px;
    align-items: end;
    gap: 17px 64px;
    margin-bottom: 58px;
    text-align: left;
  }

  ${Header} .eyebrow {
    grid-column: 1;
    grid-row: 1;
    gap: 10px;
    width: max-content;
    margin: 0;
    padding: 6px 10px 6px 7px;
    border: 1px solid rgba(17, 19, 24, 0.09);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.58);
    color: #5d6672;
    box-shadow: 0 8px 22px rgba(55, 74, 98, 0.055);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    font-size: 0.53rem;
    font-weight: 810;
    letter-spacing: 0.13em;
  }

  ${Header} .eyebrow span {
    padding: 7px 9px;
    border-radius: 999px;
    background: #18212c;
    color: #f5f8fb;
    font-size: 0.5rem;
    font-weight: 830;
    letter-spacing: 0.11em;
  }

  ${Header} .eyebrow i {
    width: 18px;
    height: 1px;
    background: rgba(40, 55, 72, 0.22);
  }

  ${Header} .eyebrow b {
    font-weight: inherit;
  }

  ${Header} h2 {
    grid-column: 1;
    grid-row: 2;
    margin: 0;
    color: #131820;
    font-size: clamp(3.1rem, 4.8vw, 4.55rem);
    font-weight: 810;
    line-height: 1.02;
    letter-spacing: -0.064em;
  }

  ${Header} h2 span {
    background: linear-gradient(95deg, #315e85, #5686aa 62%, #315e85);
    background-clip: text;
    color: transparent;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  ${Header} .header-note {
    grid-column: 2;
    grid-row: 1 / span 2;
    align-self: end;
    padding: 23px 0 7px;
    border-top: 1px solid rgba(17, 19, 24, 0.11);
  }

  ${Header} .header-note p {
    margin: 0;
    color: #646d79;
    font-size: 1rem;
    font-weight: 510;
    line-height: 1.7;
    letter-spacing: -0.02em;
  }

  ${Header} .choice-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-top: 22px;
    color: #8a929d;
    font-size: 0.67rem;
    font-weight: 680;
  }

  ${Header} .choice-summary strong {
    color: #27313d;
    font-size: 0.7rem;
    font-weight: 790;
  }

  ${OfferGrid} {
    grid-template-columns: minmax(0, 0.88fr) minmax(0, 1.12fr);
    grid-template-areas:
      'free direct'
      'bootcamp direct';
    grid-template-rows: repeat(2, minmax(260px, auto));
    max-width: 1180px;
    align-items: stretch;
    gap: 18px;
    margin-bottom: 28px;
  }

  ${OfferCard} {
    min-height: 0;
    padding: 28px;
    isolation: isolate;
    border: 1px solid rgba(17, 19, 24, 0.075);
    border-radius: 32px;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.9) inset,
      0 30px 70px rgba(43, 60, 82, 0.12),
      0 7px 20px rgba(43, 60, 82, 0.055);
    transition: border-color 300ms ease, box-shadow 300ms ease;
  }

  ${OfferCard} > * {
    position: relative;
    z-index: 2;
  }

  ${OfferCard}::after {
    content: '';
    position: absolute;
    z-index: 1;
    top: -78%;
    left: -92%;
    width: 64%;
    height: 235%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.48), transparent);
    transform: rotate(20deg);
    transition: left 760ms cubic-bezier(0.22, 1, 0.36, 1);
    pointer-events: none;
  }

  ${OfferCard}.offer-card--free {
    --offer-color: #9d6742;
    grid-area: free;
    background:
      radial-gradient(circle at 88% 8%, rgba(255, 209, 168, 0.43), transparent 34%),
      linear-gradient(145deg, #fffaf4, #f4e9dc);
  }

  ${OfferCard}.offer-card--direct {
    --offer-color: #8bc6ff;
    grid-area: direct;
    min-height: 538px;
    padding: 35px;
    border-color: rgba(255, 255, 255, 0.11);
    background:
      radial-gradient(circle at 84% 8%, rgba(87, 143, 218, 0.36), transparent 34%),
      radial-gradient(circle at 12% 86%, rgba(50, 102, 159, 0.16), transparent 32%),
      linear-gradient(145deg, #1c2734, #0f151d);
    color: #f5f8fb;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.14) inset,
      0 40px 90px rgba(20, 34, 53, 0.29),
      0 10px 28px rgba(20, 34, 53, 0.14);
  }

  ${OfferCard}.offer-card--direct::before {
    content: '';
    position: absolute;
    right: -112px;
    bottom: 96px;
    width: 300px;
    height: 300px;
    border: 1px solid rgba(144, 196, 247, 0.08);
    border-radius: 50%;
    box-shadow:
      0 0 0 46px rgba(144, 196, 247, 0.025),
      0 0 0 92px rgba(144, 196, 247, 0.018);
    pointer-events: none;
  }

  ${OfferCard}.offer-card--bootcamp {
    --offer-color: #397bcf;
    grid-area: bootcamp;
    background:
      radial-gradient(circle at 84% 4%, rgba(255, 255, 255, 0.86), transparent 35%),
      linear-gradient(145deg, #e5f2ff, #cadff5);
  }

  ${OfferCard}:hover {
    border-color: rgba(66, 111, 157, 0.18);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.92) inset,
      0 42px 88px rgba(43, 60, 82, 0.18),
      0 9px 24px rgba(43, 60, 82, 0.08);
  }

  ${OfferCard}:hover::after,
  ${OfferCard}:focus-within::after {
    left: 145%;
  }

  ${OfferCard}:focus-within {
    border-color: rgba(0, 113, 227, 0.32);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.92) inset,
      0 38px 80px rgba(43, 60, 82, 0.17);
  }

  ${OfferCard}.offer-card--direct:hover,
  ${OfferCard}.offer-card--direct:focus-within {
    border-color: rgba(139, 198, 255, 0.28);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.16) inset,
      0 48px 100px rgba(20, 34, 53, 0.36),
      0 12px 32px rgba(20, 34, 53, 0.18);
  }

  ${OfferCard} .offer-route {
    display: flex;
    min-height: 24px;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 18px;
  }

  ${OfferCard} .offer-route span {
    color: currentColor;
    font-size: 0.62rem;
    font-weight: 820;
    letter-spacing: 0.13em;
    opacity: 0.48;
  }

  ${OfferCard} .offer-route small {
    padding: 6px 9px;
    border: 1px solid rgba(17, 19, 24, 0.08);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.42);
    color: #5f6873;
    font-size: 0.62rem;
    font-weight: 720;
    letter-spacing: -0.01em;
  }

  ${OfferCard}.offer-card--direct .offer-route small {
    border-color: rgba(112, 223, 166, 0.17);
    background: rgba(80, 195, 136, 0.1);
    color: #9be2bd;
  }

  ${OfferCard} .offer-icon {
    width: 39px;
    height: 39px;
    border-color: color-mix(in srgb, var(--offer-color) 20%, transparent);
    border-radius: 13px;
    background: color-mix(in srgb, var(--offer-color) 8%, white);
    font-size: 1rem;
  }

  ${OfferCard}.offer-card--direct .offer-icon {
    border-color: rgba(139, 198, 255, 0.18);
    background: rgba(139, 198, 255, 0.09);
  }

  ${OfferCard} .offer-kicker {
    font-size: 0.58rem;
    font-weight: 820;
    letter-spacing: 0.16em;
  }

  ${OfferCard} h3,
  ${OfferCard} .price {
    color: #171b22;
  }

  ${OfferCard} h3 {
    margin-top: 6px;
    font-size: 1.42rem;
    font-weight: 800;
    letter-spacing: -0.04em;
  }

  ${OfferCard} .offer-price-stack {
    margin-top: 21px;
  }

  ${OfferCard} .price {
    margin: 0 0 5px;
    font-size: clamp(2rem, 3vw, 2.65rem);
    font-weight: 830;
    line-height: 1.02;
    letter-spacing: -0.055em;
  }

  ${OfferCard} .original {
    min-height: 0;
    color: #666f7a;
    font-size: 0.8rem;
    font-weight: 650;
    line-height: 1.45;
  }

  ${OfferCard} .consultation-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: max-content;
    margin-bottom: 13px;
    padding: 7px 10px;
    border: 1px solid rgba(112, 223, 166, 0.17);
    border-radius: 999px;
    background: rgba(80, 195, 136, 0.11);
    color: #a7e8c5;
    font-size: 0.68rem;
    font-weight: 760;
  }

  ${OfferCard}.offer-card--direct h3,
  ${OfferCard}.offer-card--direct .price {
    color: #f7f9fc;
  }

  ${OfferCard}.offer-card--direct h3 {
    font-size: clamp(2rem, 3.2vw, 2.75rem);
    line-height: 1.03;
  }

  ${OfferCard}.offer-card--direct .offer-kicker {
    color: #8bc6ff;
  }

  ${OfferCard}.offer-card--direct .offer-price-stack {
    margin-top: 48px;
  }

  ${OfferCard}.offer-card--direct .price {
    font-size: clamp(1.8rem, 3vw, 2.5rem);
    line-height: 1.2;
  }

  ${OfferCard}.offer-card--direct .original {
    color: rgba(235, 242, 249, 0.62);
    font-size: 0.82rem;
    text-decoration: none;
  }

  ${OfferCard} p {
    margin: 13px 0 17px;
    color: #5f6873;
    font-size: 0.82rem;
    font-weight: 540;
    line-height: 1.65;
  }

  ${OfferCard}.offer-card--direct p {
    max-width: 450px;
    margin-top: 19px;
    color: rgba(224, 233, 242, 0.68);
    font-size: 0.92rem;
    line-height: 1.72;
  }

  ${OfferCard} .offer-mini-flow {
    display: grid;
    grid-template-columns: auto minmax(18px, 1fr) auto minmax(18px, 1fr) auto;
    align-items: center;
    gap: 11px;
    margin: 6px 0 29px;
    padding: 16px;
    border: 1px solid rgba(139, 198, 255, 0.12);
    border-radius: 18px;
    background: rgba(4, 11, 18, 0.22);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
  }

  ${OfferCard} .offer-mini-flow span {
    display: grid;
    gap: 4px;
    color: #dce8f3;
    font-size: 0.7rem;
    font-weight: 680;
    white-space: nowrap;
  }

  ${OfferCard} .offer-mini-flow b {
    color: #7ebdff;
    font-size: 0.52rem;
    font-weight: 820;
    letter-spacing: 0.11em;
  }

  ${OfferCard} .offer-mini-flow i {
    height: 1px;
    background: linear-gradient(90deg, rgba(126, 189, 255, 0.34), rgba(126, 189, 255, 0.08));
  }

  ${OfferCard} .offer-link {
    min-height: 50px;
    border-color: rgba(17, 19, 24, 0.08);
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.62);
    color: #26313d;
    font-size: 0.76rem;
    font-weight: 760;
    box-shadow: 0 8px 22px rgba(55, 74, 98, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.72);
    transition: background 220ms ease, border-color 220ms ease, color 220ms ease, transform 220ms ease;
  }

  ${OfferCard} .offer-link svg {
    transition: transform 220ms ease;
  }

  ${OfferCard} .offer-link:hover,
  ${OfferCard} .offer-link:focus-visible {
    border-color: #18212c;
    background: #18212c;
    color: #fff;
  }

  ${OfferCard} .offer-link:hover svg,
  ${OfferCard} .offer-link:focus-visible svg {
    transform: translateX(3px);
  }

  ${OfferCard} .offer-link:focus-visible {
    outline: 3px solid rgba(0, 113, 227, 0.34);
    outline-offset: 3px;
  }

  ${OfferCard} .offer-link:active {
    transform: scale(0.985);
  }

  ${OfferCard} .offer-link:disabled,
  ${OfferCard} .offer-link:disabled:hover,
  ${OfferCard} .offer-link:disabled:focus-visible {
    border-color: rgba(57, 123, 207, 0.13);
    background: rgba(255, 255, 255, 0.38);
    color: rgba(38, 49, 61, 0.55);
    cursor: default;
    transform: none;
    outline: none;
  }

  ${OfferCard} .offer-link:disabled svg {
    transform: none;
  }

  ${OfferCard}.offer-card--direct .offer-link {
    min-height: 54px;
    border-color: rgba(255, 255, 255, 0.9);
    background: #fff;
    color: #171b22;
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.22);
  }

  ${OfferCard}.offer-card--direct .offer-link:hover,
  ${OfferCard}.offer-card--direct .offer-link:focus-visible {
    border-color: #eaf3fc;
    background: #eaf3fc;
    color: #10243b;
  }

  ${OfferCard} .consultation-actions {
    display: grid;
    gap: 10px;
    margin-top: auto;
  }

  ${OfferCard} .consultation-actions .offer-link {
    margin-top: 0;
  }

  ${OfferCard}.offer-card--direct .offer-link--secondary {
    border-color: rgba(139, 198, 255, 0.3);
    background: rgba(139, 198, 255, 0.08);
    color: #dce8f3;
    box-shadow: none;
  }

  ${OfferCard} .registration-followup {
    margin-top: 17px;
    color: rgba(235, 242, 249, 0.62);
    font-size: 0.78rem;
    text-align: center;
  }

  ${OfferCard} .registration-followup button {
    margin-left: 6px;
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: #dce8f3;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  ${OfferCard} .registration-followup button:hover {
    color: #fff;
  }

  ${OfferCard} .registration-followup button:focus-visible {
    outline: 3px solid #8bc6ff;
    outline-offset: 4px;
    border-radius: 3px;
  }

  ${Panel} {
    max-width: 1280px;
    margin: 0 auto;
    border: 1px solid rgba(75, 102, 133, 0.12);
    border-radius: 32px;
    background:
      radial-gradient(circle at 96% 0%, rgba(88, 155, 224, 0.09), transparent 30%),
      rgba(255, 255, 255, 0.9);
    box-shadow: 0 36px 100px rgba(42, 55, 73, 0.13), inset 0 1px 0 #fff;
    backdrop-filter: blur(24px) saturate(130%);
  }

  ${Panel}::before {
    height: 1px;
    background: linear-gradient(90deg, rgba(64, 116, 255, 0.42), transparent 34%, transparent 76%, rgba(115, 90, 170, 0.24));
  }

  ${ListShell} {
    padding: clamp(22px, 3.2vw, 42px);
  }

  ${ColumnHeading} {
    margin-bottom: 24px;
  }

  ${ColumnHeading} .title {
    color: #171b22;
    font-size: 1.32rem;
    font-weight: 790;
    letter-spacing: -0.035em;
  }

  ${ColumnHeading} .desc {
    color: #7a838e;
    font-weight: 560;
  }

  ${ProgramSwitch} {
    gap: 7px;
    padding: 6px;
    border-color: rgba(17, 19, 24, 0.08);
    border-radius: 19px;
    background: #eef2f6;
  }

  ${ProgramSwitch} button {
    min-height: 56px;
    border-color: transparent;
    background: transparent;
    color: #727c88;
  }

  ${ProgramSwitch} button[aria-selected='true'] {
    border-color: rgba(63, 114, 174, 0.14);
    background: #fff;
    color: #171b22;
    box-shadow: 0 8px 24px rgba(42, 55, 73, 0.1), inset 0 1px 0 #fff;
  }

  ${ProgramSwitch} button:hover,
  ${ProgramSwitch} button:focus-visible {
    border-color: rgba(64, 116, 255, 0.22);
    color: #171b22;
  }

  ${ProgramSwitch} .switch-label small {
    color: #8d96a1;
  }

  ${ProgramSwitch} .switch-count {
    background: #e7eef6;
    color: #397bcf;
  }

  ${ScheduleToolbar} {
    border-color: rgba(17, 19, 24, 0.08);
    border-radius: 23px;
    background:
      radial-gradient(circle at 0 0, rgba(73, 142, 216, 0.1), transparent 35%),
      #f6f8fa;
    box-shadow: inset 0 1px 0 #fff;
  }

  ${ScheduleToolbar} .overview strong {
    color: #252b34;
    font-weight: 780;
  }

  ${ScheduleToolbar} .overview span,
  ${ScheduleToolbar} .filter-label {
    color: #7b8590;
  }

  ${ScheduleToolbar} .filter-row {
    border-top-color: rgba(17, 19, 24, 0.075);
  }

  ${ScheduleSearch} > svg {
    color: #397bcf;
  }

  ${ScheduleSearch} input {
    border-color: rgba(75, 114, 160, 0.16);
    background: rgba(255, 255, 255, 0.9);
    color: #202630;
    font-weight: 650;
  }

  ${ScheduleSearch} input::placeholder {
    color: #9aa3ad;
  }

  ${ScheduleSearch} input:focus {
    border-color: rgba(51, 111, 181, 0.52);
    background: #fff;
    box-shadow: 0 0 0 4px rgba(64, 116, 255, 0.11);
  }

  ${ScheduleSearch} button {
    color: #7c8793;
  }

  ${ScheduleSearch} button:hover,
  ${ScheduleSearch} button:focus-visible {
    background: #edf2f7;
    color: #202630;
  }

  ${ScheduleFilterButton} {
    border-color: rgba(17, 19, 24, 0.09);
    background: rgba(255, 255, 255, 0.75);
    color: #69737f;
    box-shadow: none;
  }

  ${ScheduleFilterButton} span {
    background: #edf1f5;
  }

  ${ScheduleFilterButton}[aria-pressed='true'] {
    border-color: rgba(57, 123, 207, 0.42);
    background: #e5f0fc;
    color: #245b96;
    box-shadow: 0 8px 20px rgba(57, 123, 207, 0.1);
  }

  ${ScheduleFilterButton}[aria-pressed='true'] span {
    background: rgba(57, 123, 207, 0.12);
  }

  ${ScheduleFilterButton}:hover,
  ${ScheduleFilterButton}:focus-visible {
    border-color: rgba(57, 123, 207, 0.38);
    color: #234e7c;
  }

  ${ScheduleResultMeta} {
    color: #7c8590;
  }

  ${ScheduleResultMeta} strong {
    color: #397bcf;
  }

  ${ScheduleCollection} {
    border-color: rgba(47, 139, 104, 0.16);
    border-radius: 25px;
    background: linear-gradient(145deg, rgba(234, 247, 241, 0.78), rgba(249, 250, 250, 0.9));
  }

  ${ScheduleCollection} .collection-kicker {
    color: #2f8b68;
  }

  ${ScheduleCollection} h3 {
    color: #202630;
  }

  ${ScheduleCollection} .collection-description {
    color: #727c87;
  }

  ${ScheduleCollection} .collection-price {
    border-color: rgba(47, 139, 104, 0.18);
    background: rgba(47, 139, 104, 0.08);
    color: #28795b;
  }

  ${ScheduleCollection}#paid-bootcamp-schedules {
    border-color: rgba(112, 88, 166, 0.16);
    background: linear-gradient(145deg, rgba(241, 237, 249, 0.76), rgba(249, 249, 250, 0.9));
  }

  ${ScheduleCollection}#paid-bootcamp-schedules .collection-kicker {
    color: #7058a6;
  }

  ${ScheduleCollection}#paid-bootcamp-schedules .collection-price {
    border-color: rgba(112, 88, 166, 0.18);
    background: rgba(112, 88, 166, 0.08);
    color: #654b9e;
  }

  ${EventGroup} {
    border-color: rgba(17, 19, 24, 0.075);
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 12px 34px rgba(42, 55, 73, 0.07), inset 0 1px 0 #fff;
  }

  ${EventGroup}:hover {
    border-color: rgba(57, 123, 207, 0.2);
    box-shadow: 0 18px 44px rgba(42, 55, 73, 0.11), inset 0 1px 0 #fff;
  }

  ${EventDateBlock} {
    border-right-color: rgba(17, 19, 24, 0.07);
    background:
      radial-gradient(circle at 50% 10%, rgba(78, 149, 224, 0.14), transparent 42%),
      #edf4fb;
  }

  ${EventDateBlock} .month {
    color: #397bcf;
  }

  ${EventDateBlock} .day {
    color: #171b22;
  }

  ${EventDateBlock} .weekday {
    color: #707a86;
  }

  ${EventDateBlock} .dday {
    border-color: rgba(47, 157, 113, 0.16);
    background: #eaf7f1;
    color: #2f815f;
  }

  ${EventGroupBody} .group-title {
    color: #202630;
  }

  ${EventGroupBody} .group-hint {
    color: #8a939d;
  }

  ${EventGroupBody} .group-location {
    color: #65717d;
  }

  ${EventGroupBody} .group-location svg {
    color: #7058a6;
  }

  ${EventCategory} {
    border-color: rgba(57, 123, 207, 0.15);
    background: #edf5fd;
    color: #3971ad;
  }

  ${EventButton} {
    border-color: rgba(17, 19, 24, 0.085);
    background: #f8fafb;
    color: #202630;
    box-shadow: none;
  }

  ${EventButton} .slot-icon {
    border-color: rgba(57, 123, 207, 0.14);
    background: #eaf3fc;
    color: #397bcf;
  }

  ${EventButton} .slot-time {
    color: #202630;
  }

  ${EventButton} .slot-state {
    color: #7e8994;
  }

  ${EventButton} .slot-arrow {
    color: #397bcf;
  }

  ${EventButton}[aria-pressed='true'] {
    border-color: rgba(57, 123, 207, 0.52);
    background: linear-gradient(135deg, #e8f3ff, #edf1fb);
    box-shadow: 0 10px 26px rgba(57, 123, 207, 0.1);
  }

  ${EventButton}:disabled {
    border-color: rgba(206, 79, 79, 0.14);
    background: #fbf0f0;
  }

  ${EventButton}:disabled .slot-icon {
    border-color: rgba(206, 79, 79, 0.14);
    background: #fbecec;
    color: #c45c5c;
  }

  ${EventButton}:disabled .slot-time {
    color: #995454;
  }

  ${EventButton}:disabled .slot-state {
    color: #b15d5d;
  }

  ${EventButton}:disabled .slot-arrow {
    color: #bf7777;
  }

  ${EventButton}:hover,
  ${EventButton}:focus-visible {
    border-color: rgba(57, 123, 207, 0.42);
    background: #edf5fd;
    box-shadow: 0 12px 28px rgba(57, 123, 207, 0.1);
  }

  ${EventButton}:disabled:hover,
  ${EventButton}:disabled:focus-visible {
    border-color: rgba(206, 79, 79, 0.14);
    background: #fbf0f0;
    box-shadow: none;
  }

  ${ScheduleSkeleton} .skeleton-row {
    border-color: rgba(17, 19, 24, 0.07);
    background:
      linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent),
      #edf1f5;
    background-size: 220% 100%, auto;
  }

  ${StateAction} {
    border-color: rgba(57, 123, 207, 0.24);
    background: #e9f3fd;
    color: #326fae;
  }

  ${StateAction}:hover,
  ${StateAction}:focus-visible {
    border-color: rgba(57, 123, 207, 0.42);
    background: #deedfb;
  }

  ${EmptyState} {
    border-color: rgba(96, 112, 130, 0.22);
    background:
      radial-gradient(circle at 50% 0, rgba(78, 149, 224, 0.1), transparent 42%),
      #f6f8fa;
    color: #717c88;
  }

  ${EmptyState} .state-icon {
    border-color: rgba(57, 123, 207, 0.15);
    background: #eaf3fc;
    color: #397bcf;
  }

  ${EmptyState} strong {
    color: #252b34;
  }

  ${EmptyState} p {
    color: #7c8590;
  }

  ${MyPageNote} {
    border-color: rgba(57, 123, 207, 0.14);
    background: #edf5fc;
    color: #647383;
  }

  ${MyPageNote} a {
    color: #397bcf;
  }

  ${MyPageNote} a:hover,
  ${MyPageNote} a:focus-visible {
    color: #24558a;
  }

  @media (max-width: 991px) {
    ${Header} {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      gap: 18px;
      max-width: 760px;
    }

    ${Header} .eyebrow,
    ${Header} h2,
    ${Header} .header-note {
      grid-column: 1;
      grid-row: auto;
    }

    ${Header} .header-note {
      margin-top: 7px;
    }

    ${OfferGrid} {
      grid-template-columns: 1fr;
      grid-template-areas:
        'free'
        'direct'
        'bootcamp';
      grid-template-rows: auto;
      max-width: 760px;
    }

    ${OfferCard}.offer-card--direct {
      min-height: 500px;
    }
  }

  @media (max-width: 768px) {
    padding: 92px 0 84px;
  }

  @media (max-width: 620px) {
    ${Header} {
      margin-bottom: 34px;
      text-align: left;
    }

    ${Header} h2 {
      font-size: clamp(2.55rem, 11vw, 3.25rem);
      line-height: 1.03;
    }

    ${Header} .header-note {
      padding-top: 18px;
    }

    ${Header} .header-note p {
      font-size: 0.92rem;
    }

    ${Header} .choice-summary {
      align-items: flex-start;
      flex-direction: column;
      gap: 5px;
      margin-top: 17px;
    }

    ${OfferCard} {
      padding: 22px;
      border-radius: 26px;
    }

    ${OfferCard}.offer-card--direct {
      min-height: 0;
      padding: 24px;
    }

    ${OfferCard}.offer-card--direct h3 {
      font-size: 2rem;
    }

    ${OfferCard}.offer-card--direct .offer-price-stack {
      margin-top: 34px;
    }

    ${OfferCard}.offer-card--direct .price {
      font-size: clamp(1.75rem, 7.5vw, 2.3rem);
    }

    ${OfferCard} .offer-mini-flow {
      gap: 7px;
      padding: 14px 12px;
    }

    ${OfferCard} .offer-mini-flow span {
      font-size: 0.65rem;
      white-space: normal;
    }

    ${OfferCard} .offer-mini-flow i {
      min-width: 8px;
    }

    ${Panel} {
      border-radius: 25px;
    }

    ${ListShell} {
      padding: 18px 14px;
    }

    ${EventDateBlock} {
      border-right: 0;
      border-bottom-color: rgba(17, 19, 24, 0.07);
    }
  }

  @media (hover: none) and (pointer: coarse), (prefers-reduced-motion: reduce) {
    ${OfferCard}::after {
      display: none;
      transition: none;
    }

    ${OfferCard}:hover {
      border-color: rgba(17, 19, 24, 0.075);
    }

    ${OfferCard} .offer-link:hover svg,
    ${OfferCard} .offer-link:focus-visible svg {
      transform: none;
    }
  }
`;

const PremiumPublicApplyModal = styled(PublicApplyModal)`
  .modal-dialog {
    max-width: 780px;
  }

  .modal-content {
    border: 1px solid rgba(117, 154, 198, 0.22);
    border-radius: 29px;
    background:
      radial-gradient(circle at 12% 0%, rgba(117, 180, 245, 0.16), transparent 30%),
      linear-gradient(160deg, #fcfeff, #f1f7fd);
    color: #202630;
    box-shadow: 0 38px 90px rgba(10, 31, 58, 0.28), inset 0 1px 0 #fff;
  }

  .modal-header {
    padding: 27px 29px 12px;
    background: transparent;
  }

  .modal-body {
    padding: 15px 29px 29px;
  }

  .btn-close {
    filter: none;
    opacity: 0.62;
  }

  ${ModalTitleBlock} .eyebrow {
    color: #397bcf;
    font-size: 0.64rem;
    letter-spacing: 0.14em;
  }

  ${ModalTitleBlock} h3 {
    color: #171b22;
    font-weight: 810;
    letter-spacing: -0.035em;
  }

  ${ModalTitleBlock} p {
    color: #6f7985;
    font-weight: 540;
  }

  ${ModalEventSummary} {
    border-color: rgba(57, 123, 207, 0.15);
    background:
      radial-gradient(circle at 0 0, rgba(80, 149, 222, 0.12), transparent 42%),
      rgba(255, 255, 255, 0.7);
  }

  ${ModalEventSummary} .label {
    color: #397bcf;
  }

  ${ModalEventSummary} .title {
    color: #202630;
  }

  ${ModalEventSummary} .meta {
    color: #65717d;
  }

  ${FlowStrip} {
    border-color: rgba(17, 19, 24, 0.08);
    background: #edf2f7;
    box-shadow: inset 0 1px 0 #fff;
  }

  ${FlowStep} {
    color: #6f7985;
  }

  ${FlowStep}:last-child {
    color: #202630;
  }

  ${FlowStep}:not(:last-child)::after {
    background: linear-gradient(180deg, transparent, rgba(17, 19, 24, 0.14), transparent);
  }

  ${FlowStep} .step-index {
    border-color: rgba(57, 123, 207, 0.14);
    background: #dfeefa;
    color: #397bcf;
  }

  ${FormPanel} {
    border-color: rgba(75, 114, 160, 0.12);
    background: rgba(255, 255, 255, 0.7);
    box-shadow: inset 0 1px 0 #fff;
  }

  ${FormSectionTitle} {
    color: #252b34;
    font-weight: 790;
  }

  ${FormSectionTitle}::before {
    background: #397bcf;
    box-shadow: 0 0 0 5px rgba(57, 123, 207, 0.09);
  }

  ${FieldLabel} {
    color: #405a77;
    font-weight: 720;
  }

  ${FieldLabel} svg {
    color: #397bcf;
  }

  ${FieldControl} {
    min-height: 48px;
    border-color: rgba(75, 114, 160, 0.17) !important;
    border-radius: 13px !important;
    background: rgba(255, 255, 255, 0.88) !important;
    color: #202630 !important;
    font-weight: 640;
    box-shadow: inset 0 1px 0 #fff;
  }

  ${FieldControl}::placeholder {
    color: #a0a9b3;
    opacity: 1;
  }

  ${FieldControl}:focus {
    border-color: rgba(51, 111, 181, 0.55) !important;
    background: #fff !important;
    box-shadow: 0 0 0 4px rgba(64, 116, 255, 0.11) !important;
  }

  ${FieldControl}[aria-invalid='true'] {
    border-color: rgba(202, 72, 72, 0.72) !important;
    box-shadow: 0 0 0 4px rgba(202, 72, 72, 0.09);
  }

  ${FieldControl}[aria-invalid='true']:focus {
    border-color: #c94e4e !important;
    box-shadow: 0 0 0 4px rgba(202, 72, 72, 0.1) !important;
  }

  ${FieldErrorText} {
    color: #b44747;
  }

  ${AccountGuidance},
  ${MiniNote} {
    border-color: rgba(57, 123, 207, 0.14);
    background: #edf5fc;
    color: #647383;
  }

  ${AccountGuidance} svg,
  ${MiniNote} svg {
    color: #397bcf;
  }

  ${EventDetailBox} {
    border-color: rgba(57, 123, 207, 0.14);
    background: #edf5fc;
  }

  ${EventDetailBox} .detail-title {
    color: #2e5f91;
  }

  ${EventDetailBox} .detail-title svg,
  ${EventDetailBox} a {
    color: #397bcf;
  }

  ${EventDetailBox} .detail-body {
    color: #53687e;
  }

  ${ReferralTrigger} {
    border-color: rgba(75, 114, 160, 0.17);
    background: rgba(255, 255, 255, 0.88);
    color: #202630;
    box-shadow: inset 0 1px 0 #fff;
  }

  ${ReferralTrigger}:hover,
  ${ReferralTrigger}:focus-visible {
    border-color: rgba(51, 111, 181, 0.55);
  }

  ${ReferralTrigger}[aria-expanded='true'] {
    border-color: rgba(51, 111, 181, 0.55);
    box-shadow: 0 0 0 4px rgba(64, 116, 255, 0.11);
  }

  ${ReferralTrigger}[aria-invalid='true'] {
    border-color: rgba(202, 72, 72, 0.72);
    box-shadow: 0 0 0 4px rgba(202, 72, 72, 0.09);
  }

  ${ReferralTrigger}[aria-invalid='true']:hover,
  ${ReferralTrigger}[aria-invalid='true']:focus-visible {
    border-color: #c94e4e;
  }

  ${ReferralTrigger} svg {
    color: #397bcf;
  }

  ${ReferralMenu} {
    border-color: rgba(75, 114, 160, 0.17);
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 24px 60px rgba(42, 55, 73, 0.18), inset 0 1px 0 #fff;
  }

  ${ReferralOption} {
    border-color: transparent;
    background: transparent;
    color: #586573;
  }

  ${ReferralOption}[aria-selected='true'] {
    border-color: rgba(57, 123, 207, 0.2);
    background: #eaf3fc;
    color: #245b96;
  }

  ${ReferralOption}:hover,
  ${ReferralOption}:focus-visible {
    border-color: rgba(57, 123, 207, 0.18);
    background: #edf5fc;
    color: #245b96;
  }

  ${ReferralOption} svg {
    color: #397bcf;
  }

  ${ConsentPanel} .form-check-label {
    color: #4b5968 !important;
    font-weight: 670;
  }

  ${ConsentPanel} .form-check-input {
    border-color: rgba(75, 114, 160, 0.28);
  }

  ${ConsentPanel} .form-check-input:checked {
    border-color: #397bcf;
    background-color: #397bcf;
  }

  ${ConsentRow} {
    border-bottom-color: rgba(17, 19, 24, 0.08);
  }

  ${InlineLinkButton} {
    border-color: rgba(57, 123, 207, 0.18);
    background: #eaf3fc;
    color: #326fae;
  }

  ${InlineLinkButton}:hover,
  ${InlineLinkButton}:focus-visible {
    border-color: rgba(57, 123, 207, 0.35);
    background: #dfedf9;
    color: #245b96;
  }

  ${FormErrorSummary} {
    border-color: rgba(198, 69, 69, 0.2);
    background: #fff1f1;
    color: #8f3f3f;
    box-shadow: 0 12px 28px rgba(148, 58, 58, 0.08);
  }

  ${FormErrorSummary} strong,
  ${FormErrorSummary} .error-place {
    color: #7f3535;
  }

  ${FormErrorSummary} p,
  ${FormErrorSummary} .error-message {
    color: #a04c4c;
  }

  ${FormErrorSummary} button {
    border-color: rgba(198, 69, 69, 0.13);
    background: rgba(255, 255, 255, 0.65);
    color: #923f3f;
  }

  ${FormErrorSummary} button:hover,
  ${FormErrorSummary} button:focus-visible {
    border-color: rgba(198, 69, 69, 0.25);
    background: #fff;
  }

  ${PrimaryActionButton} {
    min-height: 52px;
    border-radius: 15px;
    background: linear-gradient(135deg, #183f6d, #347bc0);
    font-weight: 780;
    box-shadow: 0 15px 34px rgba(35, 91, 149, 0.22);
  }

  ${PrimaryActionButton}:hover,
  ${PrimaryActionButton}:focus-visible {
    box-shadow: 0 20px 44px rgba(35, 91, 149, 0.28);
  }

  ${SelectionPrompt} {
    border-color: rgba(57, 123, 207, 0.22);
    background: #f3f7fb;
    color: #697582;
  }

  ${SelectionPrompt} .icon {
    border-color: rgba(57, 123, 207, 0.15);
    background: #e6f1fb;
    color: #397bcf;
  }

  ${SelectionPrompt} strong {
    color: #252b34;
  }

  ${CompletionPanel} {
    border-color: rgba(47, 157, 113, 0.18);
    background:
      radial-gradient(circle at 0 0, rgba(77, 192, 148, 0.16), transparent 34%),
      linear-gradient(135deg, #f4fbf7, #eef6fb);
    color: #202630;
    box-shadow: 0 20px 50px rgba(42, 55, 73, 0.1), inset 0 1px 0 #fff;
  }

  ${CompletionPanel} .dismiss-button {
    border-color: rgba(17, 19, 24, 0.1);
    background: rgba(255, 255, 255, 0.72);
    color: #66717d;
  }

  ${CompletionPanel} .completion-icon {
    border-color: rgba(47, 157, 113, 0.18);
    background: #e2f5eb;
    color: #2f9d71;
  }

  ${CompletionPanel} .completion-kicker {
    color: #397bcf;
  }

  ${CompletionPanel} h4,
  ${CompletionPanel} .completion-item strong {
    color: #202630;
  }

  ${CompletionPanel} p,
  ${CompletionPanel} .completion-item small {
    color: #667481;
  }

  ${CompletionPanel} .completion-item {
    border-color: rgba(17, 19, 24, 0.08);
    background: rgba(255, 255, 255, 0.7);
  }

  ${CompletionPanel} .completion-item span {
    color: #397bcf;
  }

  ${CompletionPanel} .completion-action {
    border-color: rgba(57, 123, 207, 0.18);
    background: linear-gradient(135deg, #183f6d, #347bc0);
  }

  ${CompletionPanel} .completion-action.secondary {
    border-color: rgba(57, 123, 207, 0.18);
    background: #e9f3fd;
    color: #326fae;
  }

  .alert {
    border-radius: 15px;
    font-size: 0.86rem;
    font-weight: 620;
  }

  @media (max-width: 576px) {
    .modal-dialog {
      margin: 8px;
    }

    .modal-content {
      border-radius: 23px;
    }

    .modal-header {
      padding: 22px 18px 10px;
    }

    .modal-body {
      padding: 12px 18px 20px;
    }
  }
`;

const formatEventDate = (value) => {
  if (!value) return '일정 미확인';
  const date = new Date(value);
  return new Intl.DateTimeFormat('ko-KR', {
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  }).format(date);
};

const formatEventTime = (start, end) => {
  if (!start || !end) return '시간 미확인';
  const formatter = new Intl.DateTimeFormat('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${formatter.format(new Date(start))} - ${formatter.format(new Date(end))}`;
};

const DEFAULT_BOOTCAMP_PAYMENT_AMOUNT = 59000;
const BOOTCAMP_LOGIN_PATH = '/my-page/?login=account';
const DIRECT_MEMBERSHIP_PAYMENT_PATH = '/my-page/payment?category=membership&source=direct-registration';
const MOTION_EASE = [0.22, 1, 0.36, 1];

const sectionRevealVariants = {
  hidden: {},
  visible: {
    transition: { delayChildren: 0.04, staggerChildren: 0.09 },
  },
};

const sectionRevealItemVariants = {
  hidden: { opacity: 0, y: 26 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.72, ease: MOTION_EASE },
  },
};

const offerGridVariants = {
  hidden: {},
  visible: {
    transition: { delayChildren: 0.16, staggerChildren: 0.08 },
  },
};

const offerCardVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.985 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.62, ease: MOTION_EASE },
  },
};

const isPaymentRequiredEvent = (event) => (
  event?.payment_required === true || event?.requires_payment === true
);

const isBootcampEvent = (event) => /부트캠프|bootcamp/i.test(String(event?.title || ''));

const isPublicOtEvent = (event) => !isPaymentRequiredEvent(event) && !isBootcampEvent(event);

const resolvePaymentAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return DEFAULT_BOOTCAMP_PAYMENT_AMOUNT;
  }
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0
    ? amount
    : DEFAULT_BOOTCAMP_PAYMENT_AMOUNT;
};

const formatApplicationStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return '결제 대기';
  if (['payment_pending', 'pending_payment', 'awaiting_payment'].includes(normalized)) return '결제 대기';
  if (['confirmed', 'paid', 'payment_completed'].includes(normalized)) return '확정';
  return String(status);
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const formatEventDday = (value) => {
  if (!value) return null;
  const eventDate = new Date(value);
  if (Number.isNaN(eventDate.getTime())) return null;

  const daysLeft = Math.ceil((getStartOfDay(eventDate) - getStartOfDay(new Date())) / DAY_IN_MS);
  if (daysLeft < 0) return '종료';
  if (daysLeft === 0) return 'D-Day';
  return `D-${daysLeft}`;
};

const EVENT_LOCATION_TAG = /^(온라인|오프라인|서울|부산|대구|인천|광주|대전|울산|세종|제주|경기|강원|충북|충남|전북|전남|경북|경남)$/;

const EVENT_VENUE_BY_CATEGORY = {
  '서울/경기': '미정 (추후 공지)',
  울산: '울산 타워더모스트 팀솔루션',
};

const getPublicEventTitle = (title) => String(title || '')
  .replace(/\[(?:서울|경기)\]/g, '[서울/경기]')
  .replace(/(?:(?:원데이|여름)\s*)+부트캠프/g, '부트캠프');

const getEventTags = (title) => Array.from(String(title || '').matchAll(/\[([^\]]+)\]/g))
  .map((match) => match[1].trim())
  .filter(Boolean);

const getEventCategory = (title) => {
  const tags = getEventTags(title);
  const category = tags.find((tag) => EVENT_LOCATION_TAG.test(tag)) || tags[0] || '기타';
  return category === '서울' || category === '경기' ? '서울/경기' : category;
};

const isOfflineEventCategory = (category) => category !== '온라인' && category !== '기타';

const getEventVenue = (title) => EVENT_VENUE_BY_CATEGORY[getEventCategory(title)] || null;

const getEventDisplayTitle = (title) => {
  const publicTitle = getPublicEventTitle(title || '나니아랩 이벤트').trim();
  const category = getEventCategory(publicTitle);
  if (category === '기타') return publicTitle;
  return publicTitle.replace(`[${category}]`, '').replace(/\s{2,}/g, ' ').trim() || publicTitle;
};

const getEventDateKey = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getEventDateParts = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { month: '일정', day: '--', weekday: '미확인' };
  }
  return {
    month: `${date.getMonth() + 1}월`,
    day: String(date.getDate()),
    weekday: `${new Intl.DateTimeFormat('ko-KR', { weekday: 'short' }).format(date)}요일`,
  };
};

const groupEventSchedules = (eventList) => {
  const grouped = new Map();

  eventList.forEach((event) => {
    const category = getEventCategory(event.title);
    const displayTitle = getEventDisplayTitle(event.title);
    const dateKey = getEventDateKey(event.start_time);
    const groupKey = dateKey
      ? `${dateKey}::${category}::${displayTitle}`
      : `event::${event.id}`;

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        key: groupKey,
        category,
        displayTitle,
        dateKey,
        startTime: event.start_time,
        events: [],
      });
    }
    grouped.get(groupKey).events.push(event);
  });

  return Array.from(grouped.values()).map((group) => ({
    ...group,
    events: group.events.sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
  }));
};

const renderLinkedDetail = (value) => {
  if (!value) return null;
  return String(value).split(URL_PATTERN).map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
        >
          {part}
          <FiExternalLink aria-hidden="true" />
        </a>
      );
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const PublicEventApply = forwardRef((props, ref) => {
  const prefersReducedMotion = useReducedMotion();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [activeProgramType, setActiveProgramType] = useState('free');
  const [applicationMode, setApplicationMode] = useState('event');
  const [eventQuery, setEventQuery] = useState('');
  const [activeEventCategory, setActiveEventCategory] = useState('전체');
  const [submitting, setSubmitting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [alertInfo, setAlertInfo] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [fieldErrors, setFieldErrors] = useState({});
  const [referralMenuOpen, setReferralMenuOpen] = useState(false);
  const applicationAlertRef = useRef(null);
  const fieldRefs = useRef({});
  const errorSummaryRef = useRef(null);
  const referralSelectRef = useRef(null);

  useEffect(() => {
    // React renders this anchor after the initial document navigation.
    if (window.location.hash === '#public-event-apply') {
      document.getElementById('public-event-apply')?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      });
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadEvents = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const response = await fetch('/my-page/api/public/event-slots');
        if (!response.ok) throw new Error('event-load-failed');
        const data = await response.json();
        const eventList = Array.isArray(data.events) ? data.events : [];
        if (!mounted) return;
        setEvents(eventList);
        setActiveProgramType('free');
        setSelectedEventId((currentId) => (
          currentId && eventList.some((event) => event.id === currentId && !event.is_full)
            ? currentId
            : null
        ));
      } catch (error) {
        if (mounted) {
          setLoadError('네트워크 상태를 확인한 뒤 다시 시도해 주세요.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadEvents();
    return () => { mounted = false; };
  }, [loadAttempt]);

  useEffect(() => {
    if (alertInfo?.variant !== 'success') return undefined;
    const timer = window.setTimeout(() => {
      if (typeof applicationAlertRef.current?.scrollIntoView === 'function') {
        applicationAlertRef.current.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [alertInfo]);

  useEffect(() => {
    if (!referralMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!referralSelectRef.current?.contains(event.target)) {
        setReferralMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setReferralMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [referralMenuOpen]);

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === selectedEventId) || null,
    [events, selectedEventId]
  );
  const isDirectRegistration = applicationMode === 'direct';
  const isBootcampFlow = !isDirectRegistration && (
    isPaymentRequiredEvent(selectedEvent) || alertInfo?.paymentRequired === true
  );
  const selectedReferralOption = useMemo(
    () => REFERRAL_OPTIONS.find((option) => option.value === formData.referralSource) || null,
    [formData.referralSource]
  );
  const freeEventCount = useMemo(
    () => events.filter(isPublicOtEvent).length,
    [events]
  );
  const programEvents = useMemo(
    () => events.filter((event) => (
      activeProgramType === 'paid'
        ? isPaymentRequiredEvent(event)
        : isPublicOtEvent(event)
    )),
    [activeProgramType, events]
  );
  const eventCategoryOptions = useMemo(() => {
    const counts = new Map();
    programEvents.forEach((event) => {
      const category = getEventCategory(event.title);
      counts.set(category, (counts.get(category) || 0) + 1);
    });
    const options = [{ label: '전체', count: programEvents.length }];
    const onlineCount = counts.get('온라인') || 0;
    const offlineCount = Array.from(counts).reduce(
      (total, [category, count]) => total + (isOfflineEventCategory(category) ? count : 0),
      0
    );

    if (onlineCount > 0) options.push({ label: '온라인', count: onlineCount });
    if (offlineCount > 0) options.push({ label: '오프라인', count: offlineCount });
    Array.from(counts).forEach(([label, count]) => {
      if (label !== '온라인' && label !== '오프라인') options.push({ label, count });
    });
    return options;
  }, [programEvents]);
  const filteredEvents = useMemo(() => {
    const normalizedQuery = eventQuery.trim().toLocaleLowerCase('ko-KR');
    return programEvents.filter((event) => {
      const category = getEventCategory(event.title);
      if (activeEventCategory === '오프라인' && !isOfflineEventCategory(category)) return false;
      if (
        activeEventCategory !== '전체'
        && activeEventCategory !== '오프라인'
        && category !== activeEventCategory
      ) return false;
      if (!normalizedQuery) return true;
      const searchableText = [
        event.title,
        getEventDisplayTitle(event.title),
        category,
        isOfflineEventCategory(category) ? '오프라인' : '',
        getEventVenue(event.title),
        formatEventDate(event.start_time),
        formatEventTime(event.start_time, event.end_time),
      ].join(' ').toLocaleLowerCase('ko-KR');
      return searchableText.includes(normalizedQuery);
    });
  }, [activeEventCategory, eventQuery, programEvents]);
  const groupedEvents = useMemo(() => groupEventSchedules(filteredEvents), [filteredEvents]);
  const availableEventCount = useMemo(
    () => programEvents.filter((event) => !event.is_full).length,
    [programEvents]
  );

  const setFieldRef = (fieldName) => (node) => {
    if (node) fieldRefs.current[fieldName] = node;
  };

  const focusField = (fieldName) => {
    const node = fieldRefs.current[fieldName];
    if (!node) return;
    if (typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (typeof node.focus === 'function') {
      node.focus({ preventScroll: true });
    }
  };

  const focusFirstFieldError = (errors) => {
    const firstError = getFieldErrorList(errors)[0];
    if (firstError) focusField(firstError.field);
  };

  const getFieldStatusProps = (fieldName) => {
    const hasError = Boolean(fieldErrors[fieldName]);
    return {
      ref: setFieldRef(fieldName),
      $hasError: hasError,
      isInvalid: hasError,
      'aria-invalid': hasError,
      'aria-describedby': hasError ? `public-apply-${fieldName}-error` : undefined,
    };
  };

  const renderFieldError = (fieldName) => (
    fieldErrors[fieldName] ? (
      <FieldErrorText id={`public-apply-${fieldName}-error`}>
        <FiAlertCircle aria-hidden="true" />
        <span>{fieldErrors[fieldName]}</span>
      </FieldErrorText>
    ) : null
  );

  const buildValidationErrors = () => {
    const errors = {};
    const trimValue = (fieldName) => String(formData[fieldName] || '').trim();
    const digits = (value) => value.replace(/\D/g, '');

    if (!trimValue('applicantName')) errors.applicantName = '보호자 이름을 입력해 주세요.';

    if (!trimValue('parentEmail')) {
      errors.parentEmail = '학부모 이메일을 입력해 주세요.';
    } else if (!EMAIL_PATTERN.test(trimValue('parentEmail'))) {
      errors.parentEmail = '학부모 이메일 형식을 확인해 주세요.';
    }

    if (!trimValue('phoneNumber')) {
      errors.phoneNumber = '전화번호를 입력해 주세요.';
    } else if (digits(trimValue('phoneNumber')).length < 8) {
      errors.phoneNumber = '전화번호는 숫자 8자리 이상으로 입력해 주세요.';
    }

    if (!trimValue('childName')) errors.childName = '자녀 이름을 입력해 주세요.';
    if (!trimValue('childGrade')) errors.childGrade = '지역 및 학교/학년을 입력해 주세요.';

    if (trimValue('childPhone') && digits(trimValue('childPhone')).length < 8) {
      errors.childPhone = '자녀 전화번호는 숫자 8자리 이상으로 입력해 주세요.';
    }
    if (trimValue('childEmail') && !EMAIL_PATTERN.test(trimValue('childEmail'))) {
      errors.childEmail = '자녀 이메일 형식을 확인해 주세요.';
    }

    if (!trimValue('username')) {
      errors.username = '자녀가 사용할 아이디를 입력해 주세요.';
    } else if (!USERNAME_PATTERN.test(trimValue('username'))) {
      errors.username = '아이디는 소문자로 시작하고, 소문자·숫자·언더스코어·하이픈만 사용할 수 있습니다.';
    }

    if (!formData.password) {
      errors.password = '비밀번호를 입력해 주세요.';
    } else if (formData.password.length < 8) {
      errors.password = '비밀번호는 8자 이상으로 입력해 주세요.';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = '비밀번호 확인을 입력해 주세요.';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = '비밀번호와 비밀번호 확인이 일치하지 않습니다.';
    }

    if (!formData.referralSource) {
      errors.referralSource = '나니아랩을 알게 된 경로를 선택해 주세요.';
    }
    if (formData.referralSource === 'other' && !trimValue('referralOther')) {
      errors.referralOther = '기타 유입 경로를 입력해 주세요.';
    }

    if (!formData.agreeTerms) errors.agreeTerms = '서비스 이용약관에 동의해 주세요.';
    if (!formData.agreePrivacy) errors.agreePrivacy = '개인정보 처리방침에 동의해 주세요.';

    return errors;
  };

  const applyFieldErrors = (errors) => {
    setFieldErrors(errors);
    focusFirstFieldError(errors);
  };

  const getApiErrorMessage = (data, fallback) => {
    const detail = data?.detail || data?.message;
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || item?.message || String(item)).join(' ');
    }
    if (detail && typeof detail === 'object') {
      return detail.msg || detail.message || fallback;
    }
    return detail || fallback;
  };

  const getSignupErrorField = (message) => {
    const normalized = String(message || '').toLowerCase();
    if (/아이디|username|account_username/.test(normalized)) return 'username';
    if (/비밀번호|password/.test(normalized)) return 'password';
    if (/이메일|email/.test(normalized)) return 'parentEmail';
    if (/전화|phone/.test(normalized)) return 'phoneNumber';
    if (/이름|name/.test(normalized)) return 'applicantName';
    return null;
  };

  const clearFieldErrors = (...fieldNames) => {
    const names = fieldNames.filter(Boolean);
    setFieldErrors((prev) => {
      if (!names.some((fieldName) => prev[fieldName])) return prev;
      const next = { ...prev };
      names.forEach((fieldName) => {
        delete next[fieldName];
      });
      return next;
    });
  };

  const handleEventSelect = (event) => {
    if (event.is_full) return;
    setApplicationMode('event');
    setSelectedEventId(event.id);
    setAlertInfo(null);
    setFieldErrors({});
    setShowApplyModal(true);
  };

  const handleDirectRegistration = () => {
    setApplicationMode('direct');
    setSelectedEventId(null);
    setAlertInfo(null);
    setFieldErrors({});
    setFormData(INITIAL_FORM_DATA);
    setValidated(false);
    setShowApplyModal(true);
  };

  const resetEventFilters = () => {
    setEventQuery('');
    setActiveEventCategory('전체');
  };

  const handleProgramTypeSelect = (programType, shouldScroll = false) => {
    setActiveProgramType(programType);
    setEventQuery('');
    setActiveEventCategory('전체');
    if (!shouldScroll) return;
    window.setTimeout(() => {
      document.getElementById('application-schedules')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  const handleInputChange = (event) => {
    const { checked, name, type, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    clearFieldErrors(name);
  };

  const handleReferralSelect = (value) => {
    setFormData((prev) => ({
      ...prev,
      referralSource: value,
      referralOther: value === 'other' ? prev.referralOther : '',
    }));
    clearFieldErrors('referralSource', value === 'other' ? null : 'referralOther');
    setReferralMenuOpen(false);
  };

  const handleReferralTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setReferralMenuOpen(true);
    }
  };

  const validateSignupFields = () => {
    const errors = buildValidationErrors();
    if (getFieldErrorList(errors).length === 0) {
      setFieldErrors({});
      return true;
    }

    applyFieldErrors(errors);
    return false;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setValidated(true);
    setAlertInfo(null);

    if (!isDirectRegistration && (!selectedEvent || selectedEvent.is_full)) {
      setAlertInfo({
        variant: 'danger',
        heading: '신청 가능한 일정을 선택해 주세요',
        message: '마감되지 않은 나니아 과정 일정을 선택해야 신청할 수 있습니다.',
      });
      return;
    }

    if (!validateSignupFields()) return;

    setSubmitting(true);
    try {
      const username = formData.username.trim();
      const referralSource = formData.referralSource === 'other'
        ? formData.referralOther.trim()
        : formData.referralSource;

      if (isDirectRegistration) {
        const registrationResponse = await fetch('/my-page/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parent_name: formData.applicantName.trim(),
            parent_email: formData.parentEmail.trim(),
            phone: formData.phoneNumber.trim(),
            student_name: formData.childName.trim(),
            child_grade: formData.childGrade.trim(),
            child_phone: formData.childPhone.trim() || null,
            child_email: formData.childEmail.trim() || null,
            username,
            password: formData.password,
            referral_source: referralSource,
            registration_type: 'youth',
            agree_terms: formData.agreeTerms,
            agree_privacy: formData.agreePrivacy,
          }),
        });
        const registrationData = await registrationResponse.json().catch(() => ({}));
        if (!registrationResponse.ok) {
          const message = getApiErrorMessage(registrationData, '회원가입 처리 중 오류가 발생했습니다.');
          const fieldName = getSignupErrorField(message);
          if (fieldName) {
            applyFieldErrors({ [fieldName]: message });
            return;
          }
          throw new Error(message);
        }

        let accessToken = null;
        window.localStorage.removeItem('access_token');
        try {
          const loginResponse = await fetch('/my-page/api/auth/account-login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password: formData.password }),
          });
          const loginData = await loginResponse.json().catch(() => ({}));
          accessToken = loginResponse.ok ? loginData.access_token : null;
          if (accessToken) window.localStorage.setItem('access_token', accessToken);
        } catch {
          // The account already exists at this point. Keep the successful registration
          // state and let the user continue through the explicit login fallback.
        }

        setAlertInfo({
          variant: 'success',
          heading: accessToken ? '회원가입 완료 · 결제 준비 완료' : '회원가입이 완료되었습니다',
          accountUsername: username,
          eventTitle: '청소년 프로젝트 정기과정',
          paymentRequired: true,
          applicationStatus: '회원가입 완료',
          directRegistration: true,
          checkoutReady: Boolean(accessToken),
          paymentPath: accessToken ? DIRECT_MEMBERSHIP_PAYMENT_PATH : BOOTCAMP_LOGIN_PATH,
          message: accessToken
            ? '계정이 생성되고 결제 화면에 로그인할 준비가 끝났습니다. 상담에서 안내받은 내용과 결제 금액을 확인한 뒤 결제를 완료하면 정기과정 등록이 확정됩니다.'
            : '계정 생성은 완료되었지만 자동 로그인에 실패했습니다. 생성한 아이디와 비밀번호로 my-page에 로그인한 뒤 정규과정 결제를 이어가 주세요.',
        });
        setFormData(INITIAL_FORM_DATA);
        setValidated(false);
        setFieldErrors({});
        return;
      }

      const response = await fetch('/my-page/api/public/event-signups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          availability_id: selectedEvent.id,
          parent_name: formData.applicantName.trim(),
          parent_email: formData.parentEmail.trim(),
          phone: formData.phoneNumber.trim(),
          student_name: formData.childName.trim(),
          child_grade: formData.childGrade.trim(),
          child_phone: formData.childPhone.trim() || null,
          child_email: formData.childEmail.trim() || null,
          username,
          password: formData.password,
          referral_source: referralSource,
          registration_type: 'youth',
          agree_terms: formData.agreeTerms,
          agree_privacy: formData.agreePrivacy,
          source: 'apply_page',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = getApiErrorMessage(data, '계정 생성과 신청 처리 중 오류가 발생했습니다.');
        const fieldName = getSignupErrorField(message);
        if (fieldName) {
          applyFieldErrors({ [fieldName]: message });
          return;
        }
        throw new Error(message);
      }
      const paymentRequired = data.payment_required === true
        || data.requires_payment === true
        || selectedEvent.payment_required === true
        || selectedEvent.requires_payment === true;
      const paymentAmount = resolvePaymentAmount(data.payment_amount);
      const applicationStatus = data.application_status
        || (paymentRequired ? 'payment_pending' : 'confirmed');

      setAlertInfo({
        variant: 'success',
        heading: paymentRequired
          ? '신청 및 계정 생성 완료'
          : '신청이 완료되었습니다',
        accountUsername: data.account_username || username,
        eventTitle: getPublicEventTitle(selectedEvent.title),
        eventDate: formatEventDate(selectedEvent.start_time),
        eventTime: formatEventTime(selectedEvent.start_time, selectedEvent.end_time),
        paymentRequired,
        paymentAmount,
        applicationStatus,
        message: paymentRequired
          ? '결제가 필요합니다. my-page에 생성한 계정으로 로그인한 뒤 결제를 완료하면 참가가 최종 확정됩니다.'
          : '계정 생성과 나니아 과정 신청이 함께 처리되었습니다. 같은 아이디와 비밀번호로 my-page와 AI server에 로그인할 수 있습니다.',
      });
      setFormData(INITIAL_FORM_DATA);
      setValidated(false);
      setFieldErrors({});
      setEvents((prev) => prev.map((item) => (
        item.id === selectedEvent.id
          ? {
              ...item,
              remaining_spots: Math.max(0, (item.remaining_spots || 0) - 1),
              booked_count: (item.booked_count || 0) + 1,
              is_full: Math.max(0, (item.remaining_spots || 0) - 1) <= 0,
            }
          : item
      )));
    } catch (error) {
      setAlertInfo({
        variant: 'danger',
        heading: '신청을 완료하지 못했습니다',
        message: error.message || '잠시 후 다시 시도해 주세요.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderApplicationAlert = () => {
    if (!alertInfo) return null;

    if (alertInfo.variant === 'success') {
      return (
        <CompletionPanel
          ref={applicationAlertRef}
          className="mt-4"
          role="status"
          aria-label="신청 완료 안내"
          aria-atomic="true"
          initial={{ opacity: 0, y: 18, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.48, ease: MOTION_EASE }}
        >
          <button
            type="button"
            className="dismiss-button"
            aria-label="완료 안내 닫기"
            onClick={() => setAlertInfo(null)}
          >
            <FiX aria-hidden="true" />
          </button>
          <div className="completion-head">
            <div className="completion-icon" aria-hidden="true">
              <FiCheckCircle />
            </div>
            <div>
              <div className="completion-kicker">
                {alertInfo.directRegistration
                  ? 'Checkout Ready'
                  : alertInfo.paymentRequired ? 'Payment Required' : 'Reserved'}
              </div>
              <h4>{alertInfo.heading}</h4>
              <p>{alertInfo.message}</p>
            </div>
          </div>
          <div className="completion-grid">
            <div className="completion-item">
              <span>공통 로그인 계정</span>
              <strong>{alertInfo.accountUsername}</strong>
              <small>
                {alertInfo.directRegistration
                  ? alertInfo.checkoutReady ? '결제 화면 로그인 준비 완료' : 'my-page 로그인 필요'
                  : alertInfo.paymentRequired ? 'my-page 로그인 후 결제' : 'my-page · AI server'}
              </small>
            </div>
            <div className="completion-item">
              <span>
                {alertInfo.directRegistration
                  ? '다음 단계'
                  : alertInfo.paymentRequired ? '신청 및 결제 상태' : '예약 일정'}
              </span>
              <strong>{alertInfo.eventTitle}</strong>
              <small>
                {alertInfo.directRegistration
                  ? '상담에서 안내받은 수강료를 결제 화면에서 확인해 주세요.'
                  : [alertInfo.eventDate, alertInfo.eventTime].filter(Boolean).join(' · ')}
              </small>
              {alertInfo.paymentRequired && !alertInfo.directRegistration && (
                <small>현재 상태: {formatApplicationStatus(alertInfo.applicationStatus)}</small>
              )}
            </div>
          </div>
          <div className={`completion-actions${alertInfo.paymentRequired ? ' single' : ''}`}>
            {alertInfo.directRegistration ? (
              <a href={alertInfo.paymentPath} className="completion-action">
                {alertInfo.checkoutReady ? '결제하고 등록 완료하기' : 'my-page 로그인 후 결제 계속하기'}
                <FiArrowRight aria-hidden="true" />
              </a>
            ) : alertInfo.paymentRequired ? (
              <a
                href={BOOTCAMP_LOGIN_PATH}
                className="completion-action"
                onClick={() => window.localStorage.removeItem('access_token')}
              >
                my-page 로그인 후 결제 <FiExternalLink aria-hidden="true" />
              </a>
            ) : (
              <>
                <a href="/my-page/" target="_blank" rel="noopener noreferrer" className="completion-action">
                  my-page에서 확인 <FiExternalLink aria-hidden="true" />
                </a>
                <a href="https://narnia-lab.com/server/" target="_blank" rel="noopener noreferrer" className="completion-action secondary">
                  AI server 접속 <FiExternalLink aria-hidden="true" />
                </a>
              </>
            )}
          </div>
        </CompletionPanel>
      );
    }

    return (
      <Alert
        className="mt-4"
        variant={alertInfo.variant}
        onClose={() => setAlertInfo(null)}
        dismissible
      >
        <div className="d-flex align-items-start gap-3">
          <div style={{ fontSize: '1.4rem', lineHeight: 1 }}>
            <FiAlertCircle />
          </div>
          <div>
            <Alert.Heading style={{ fontSize: '1rem', fontWeight: 950 }}>{alertInfo.heading}</Alert.Heading>
            <p className="mb-0" style={{ fontWeight: 700, lineHeight: 1.6 }}>{alertInfo.message}</p>
          </div>
        </div>
      </Alert>
    );
  };

  const renderFormErrorSummary = () => {
    const errors = getFieldErrorList(fieldErrors);
    if (errors.length === 0) return null;

    return (
      <FormErrorSummary
        ref={errorSummaryRef}
        role="alert"
        aria-label="입력 오류 안내"
        aria-live="assertive"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: MOTION_EASE }}
      >
        <div className="summary-head">
          <span className="summary-icon" aria-hidden="true">
            <FiAlertCircle />
          </span>
          <div>
            <strong>입력값을 확인해 주세요</strong>
            <p>문제가 있는 항목을 누르면 해당 입력칸으로 바로 이동합니다.</p>
          </div>
        </div>
        <div className="error-list">
          {errors.map((error) => (
            <button
              key={error.field}
              type="button"
              onClick={() => focusField(error.field)}
              aria-label={`${error.section} · ${error.label}: ${error.message}`}
            >
              <span className="error-place">{error.section} · {error.label}</span>
              <span className="error-message">{error.message}</span>
            </button>
          ))}
        </div>
      </FormErrorSummary>
    );
  };

  const renderApplicationForm = () => (
    <>
      {isDirectRegistration ? (
        <ModalEventSummary>
          <div className="label">바로 등록 과정</div>
          <div className="title">청소년 프로젝트 정기과정</div>
          <div className="meta">
            <span><FiZap />회원가입 후 바로 결제</span>
            <span><FiCreditCard />상담에서 커리큘럼과 수강료 안내</span>
          </div>
        </ModalEventSummary>
      ) : selectedEvent && (
        <ModalEventSummary>
          <div className="label">선택한 일정</div>
          <div className="title">{getPublicEventTitle(selectedEvent.title)}</div>
          <div className="meta">
            <span><FiCalendar />{formatEventDate(selectedEvent.start_time)}</span>
            <span><FiClock />{formatEventTime(selectedEvent.start_time, selectedEvent.end_time)}</span>
            {getEventVenue(selectedEvent.title) && (
              <span><FiMapPin />장소 · {getEventVenue(selectedEvent.title)}</span>
            )}
          </div>
        </ModalEventSummary>
      )}

      {!isDirectRegistration && !isBootcampFlow && <ParentConsultation />}

      {selectedEvent?.detail && (
        <EventDetailBox aria-label="일정 상세 안내">
          <div className="detail-title"><FiFileText aria-hidden="true" />일정 상세 안내</div>
          <div className="detail-body">{renderLinkedDetail(selectedEvent.detail)}</div>
        </EventDetailBox>
      )}

      <FlowStrip aria-label="신청 진행 단계">
        <FlowStep>
          <span className="step-index">01</span>
          <span className="step-label">{isDirectRegistration ? '회원 정보' : '일정 확인'}</span>
        </FlowStep>
        <FlowStep>
          <span className="step-index">02</span>
          <span className="step-label">{isDirectRegistration ? '계정 생성' : '자녀 계정 입력'}</span>
        </FlowStep>
        <FlowStep>
          <span className="step-index">03</span>
          <span className="step-label">
            {isDirectRegistration ? '결제 후 등록' : isBootcampFlow ? '결제 후 확정' : '신청 완료'}
            <FiArrowRight aria-hidden="true" />
          </span>
        </FlowStep>
      </FlowStrip>

      {alertInfo?.variant === 'success' ? (
        renderApplicationAlert()
      ) : (
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        {renderFormErrorSummary()}

        <FormPanel aria-labelledby="public-apply-parent-section">
          <FormSectionTitle id="public-apply-parent-section">보호자 정보</FormSectionTitle>
          <FieldGrid>
            <FormField $full>
              <FieldLabel><FiUser /> 이름</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('applicantName')}
                required
                name="applicantName"
                value={formData.applicantName}
                onChange={handleInputChange}
                placeholder="예: 홍길동"
                autoComplete="name"
              />
              {renderFieldError('applicantName')}
            </FormField>

            <FormField>
              <FieldLabel><FiPhone /> 전화번호</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('phoneNumber')}
                required
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                type="tel"
                placeholder="예: 010-1234-5678"
                autoComplete="tel"
                minLength={8}
              />
              {renderFieldError('phoneNumber')}
            </FormField>

            <FormField>
              <FieldLabel><FiMail /> 학부모 이메일</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('parentEmail')}
                required
                name="parentEmail"
                value={formData.parentEmail}
                onChange={handleInputChange}
                type="email"
                placeholder="예: parent@example.com"
                autoComplete="email"
              />
              {renderFieldError('parentEmail')}
            </FormField>

            <FormField $full>
              <FieldLabel><FiSearch /> 나니아랩을 알게 된 경로</FieldLabel>
              <ReferralSelectShell ref={referralSelectRef} $open={referralMenuOpen}>
                <ReferralTrigger
                  ref={setFieldRef('referralSource')}
                  type="button"
                  $empty={!selectedReferralOption}
                  $hasError={Boolean(fieldErrors.referralSource)}
                  $open={referralMenuOpen}
                  aria-haspopup="listbox"
                  aria-expanded={referralMenuOpen}
                  aria-controls="public-apply-referral-options"
                  aria-label={`나니아랩을 알게 된 경로 선택: ${selectedReferralOption?.label || '선택해 주세요'}`}
                  aria-invalid={Boolean(fieldErrors.referralSource)}
                  aria-describedby={fieldErrors.referralSource ? 'public-apply-referralSource-error' : undefined}
                  onClick={() => setReferralMenuOpen((open) => !open)}
                  onKeyDown={handleReferralTriggerKeyDown}
                >
                  <span className="select-value">{selectedReferralOption?.label || '선택해 주세요'}</span>
                  <FiChevronDown aria-hidden="true" />
                </ReferralTrigger>
                {referralMenuOpen && (
                  <ReferralMenu
                    id="public-apply-referral-options"
                    role="listbox"
                    aria-label="나니아랩을 알게 된 경로"
                  >
                    {REFERRAL_OPTIONS.map((option) => {
                      const selected = formData.referralSource === option.value;
                      return (
                        <ReferralOption
                          key={option.value}
                          type="button"
                          role="option"
                          $selected={selected}
                          aria-selected={selected}
                          onClick={() => handleReferralSelect(option.value)}
                        >
                          <span>{option.label}</span>
                          <FiCheck aria-hidden="true" />
                        </ReferralOption>
                      );
                    })}
                  </ReferralMenu>
                )}
              </ReferralSelectShell>
              {renderFieldError('referralSource')}
            </FormField>

            {formData.referralSource === 'other' && (
              <FormField $full>
                <FieldLabel><FiFileText /> 기타 경로</FieldLabel>
                <FieldControl
                  {...getFieldStatusProps('referralOther')}
                  required
                  name="referralOther"
                  value={formData.referralOther}
                  onChange={handleInputChange}
                  placeholder="알게 된 경로를 입력해 주세요"
                />
                {renderFieldError('referralOther')}
              </FormField>
            )}
          </FieldGrid>
        </FormPanel>

        <FormPanel aria-labelledby="public-apply-child-section">
          <FormSectionTitle id="public-apply-child-section">자녀 정보</FormSectionTitle>
          <FieldGrid>
            <FormField>
              <FieldLabel><FiUser /> 자녀 이름</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('childName')}
                required
                name="childName"
                value={formData.childName}
                onChange={handleInputChange}
                placeholder="예: 홍나리"
              />
              {renderFieldError('childName')}
            </FormField>

            <FormField>
              <FieldLabel><FiFileText /> 지역 및 학교/학년</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('childGrade')}
                required
                name="childGrade"
                value={formData.childGrade}
                onChange={handleInputChange}
                placeholder="예: 서울 OO초 5학년"
              />
              {renderFieldError('childGrade')}
            </FormField>

            <FormField>
              <FieldLabel><FiPhone /> 자녀 전화번호</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('childPhone')}
                name="childPhone"
                value={formData.childPhone}
                onChange={handleInputChange}
                type="tel"
                placeholder="선택 입력"
              />
              {renderFieldError('childPhone')}
            </FormField>

            <FormField>
              <FieldLabel><FiMail /> 자녀 이메일</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('childEmail')}
                name="childEmail"
                value={formData.childEmail}
                onChange={handleInputChange}
                type="email"
                placeholder="선택 입력"
              />
              {renderFieldError('childEmail')}
            </FormField>
          </FieldGrid>
        </FormPanel>

        <FormPanel aria-labelledby="public-apply-account-section">
          <FormSectionTitle id="public-apply-account-section">계정 정보</FormSectionTitle>
          <AccountGuidance>
            <FiKey aria-hidden="true" />
            <span>
              {isDirectRegistration
                ? '이 계정으로 바로 my-page 결제 화면에 로그인합니다. 결제를 완료하면 정기과정 멤버십이 활성화되고 AI server를 같은 계정으로 사용할 수 있습니다.'
                : isBootcampFlow
                ? '이 계정은 부트캠프 신청과 결제에 사용할 my-page 계정입니다. 신청 및 계정 생성 후 my-page에서 로그인해 결제를 완료하면 참가가 최종 확정되며, 서버 실제 사용시간 5시간이 지급됩니다.'
                : '이 계정은 자녀가 my-page와 AI server에서 함께 사용할 계정입니다. 일정 확인과 학습 시스템 이용에 계속 쓰이니 자녀가 기억하고 입력하기 쉬운 아이디와 비밀번호로 만들어 주세요.'}
            </span>
          </AccountGuidance>
          <FieldGrid>
            <FormField $full>
              <FieldLabel><FiUser /> 아이디</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('username')}
                required
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="예: honggildong"
                autoComplete="username"
              />
              {renderFieldError('username')}
            </FormField>

            <FormField>
              <FieldLabel><FiLock /> 비밀번호</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('password')}
                required
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                type="password"
                placeholder="8자 이상"
                autoComplete="new-password"
                minLength={8}
              />
              {renderFieldError('password')}
            </FormField>

            <FormField>
              <FieldLabel><FiLock /> 비밀번호 확인</FieldLabel>
              <FieldControl
                {...getFieldStatusProps('confirmPassword')}
                required
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                type="password"
                placeholder="다시 입력"
                autoComplete="new-password"
                minLength={8}
              />
              {renderFieldError('confirmPassword')}
            </FormField>
          </FieldGrid>

          <MiniNote className="mt-3">
            <FiShield /> {isDirectRegistration
              ? '회원가입 완료 후 결제 단계로 이어집니다. 상담에서 안내받은 과정과 수강료를 확인한 뒤 등록을 결정해 주세요.'
              : isBootcampFlow
              ? '입력한 아이디와 비밀번호로 신청 계정을 만든 뒤, my-page에 로그인해 결제를 완료하면 참가 확정과 함께 서버 실제 사용시간 5시간이 지급됩니다.'
              : '입력한 아이디와 비밀번호로 계정이 만들어지고, 신청 완료 후 같은 계정으로 my-page와 AI server에 로그인할 수 있습니다.'}
          </MiniNote>
        </FormPanel>

        <FormPanel aria-labelledby="public-apply-consent-section">
          <FormSectionTitle id="public-apply-consent-section">약관 동의</FormSectionTitle>
          <ConsentPanel>
            <ConsentRow>
              <div>
                <Form.Check
                  ref={setFieldRef('agreeTerms')}
                  required
                  type="checkbox"
                  id="public-apply-agree-terms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  label="서비스 이용약관에 동의합니다."
                  isInvalid={Boolean(fieldErrors.agreeTerms)}
                  aria-invalid={Boolean(fieldErrors.agreeTerms)}
                  aria-describedby={fieldErrors.agreeTerms ? 'public-apply-agreeTerms-error' : undefined}
                />
                {renderFieldError('agreeTerms')}
              </div>
              <InlineLinkButton type="button" onClick={() => setShowTerms(true)}>
                이용약관 보기
              </InlineLinkButton>
            </ConsentRow>
            <ConsentRow>
              <div>
                <Form.Check
                  ref={setFieldRef('agreePrivacy')}
                  required
                  type="checkbox"
                  id="public-apply-agree-privacy"
                  name="agreePrivacy"
                  checked={formData.agreePrivacy}
                  onChange={handleInputChange}
                  label="개인정보 처리방침에 동의합니다."
                  isInvalid={Boolean(fieldErrors.agreePrivacy)}
                  aria-invalid={Boolean(fieldErrors.agreePrivacy)}
                  aria-describedby={fieldErrors.agreePrivacy ? 'public-apply-agreePrivacy-error' : undefined}
                />
                {renderFieldError('agreePrivacy')}
              </div>
              <InlineLinkButton type="button" onClick={() => setShowPrivacy(true)}>
                개인정보 보기
              </InlineLinkButton>
            </ConsentRow>
          </ConsentPanel>
        </FormPanel>

        {renderApplicationAlert()}

        <PrimaryActionButton
          type="submit"
          disabled={submitting || (!isDirectRegistration && (!selectedEvent || selectedEvent.is_full))}
        >
          {submitting
            ? isDirectRegistration ? '계정과 결제 준비 중...' : '처리 중...'
            : isDirectRegistration ? '계정 만들고 결제 단계로' : '계정 만들고 나니아 과정 신청'}
          <FiArrowRight />
        </PrimaryActionButton>
      </Form>
      )}
    </>
  );

  const renderEventGroups = (groups) => (
    <EventList>
      {groups.map((group, groupIndex) => {
        const dateParts = getEventDateParts(group.startTime);
        const ddayLabel = formatEventDday(group.startTime);
        const venue = getEventVenue(group.events[0]?.title);
        return (
          <EventGroup
            key={group.key}
            layout="position"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.44,
              ease: MOTION_EASE,
              delay: Math.min(groupIndex * 0.06, 0.24),
            }}
            aria-label={`${getPublicEventTitle(group.events[0]?.title) || group.displayTitle} ${formatEventDate(group.startTime)}`}
          >
            <EventDateBlock as="time" dateTime={group.dateKey || undefined}>
              <span className="month">{dateParts.month}</span>
              <span className="day">{dateParts.day}</span>
              <span className="weekday">{dateParts.weekday}</span>
              {ddayLabel && <span className="dday">{ddayLabel}</span>}
            </EventDateBlock>
            <EventGroupBody>
              <div className="group-head">
                <div className="group-title-wrap">
                  <div className="group-title">{getPublicEventTitle(group.events[0]?.title) || group.displayTitle}</div>
                  <div className="group-hint">
                    {group.events.length > 1
                      ? `${group.events.length}개 시간 중 선택해 주세요.`
                      : '아래 시간을 누르면 신청서를 작성할 수 있습니다.'}
                  </div>
                  {venue && (
                    <div className="group-location">
                      <FiMapPin aria-hidden="true" />장소 · {venue}
                    </div>
                  )}
                </div>
                <EventCategory><FiMapPin aria-hidden="true" />{group.category}</EventCategory>
              </div>
              <EventSlotGrid>
                {group.events.map((event) => {
                  const active = selectedEventId === event.id;
                  const eventTime = formatEventTime(event.start_time, event.end_time);
                  return (
                    <EventButton
                      key={event.id}
                      type="button"
                      $active={active}
                      $full={event.is_full}
                      disabled={event.is_full}
                      onClick={() => handleEventSelect(event)}
                      whileTap={event.is_full ? undefined : { scale: 0.985 }}
                      aria-pressed={active}
                      aria-label={`${getPublicEventTitle(event.title)}, ${formatEventDate(event.start_time)}, ${eventTime}${event.is_full ? ', 마감' : ', 신청하기'}`}
                    >
                      <span className="slot-icon"><FiClock aria-hidden="true" /></span>
                      <span className="slot-copy">
                        <span className="slot-time">{eventTime}</span>
                        <span className="slot-state">{event.is_full ? '신청 마감' : '이 시간으로 신청'}</span>
                      </span>
                      <span className="slot-arrow" aria-hidden="true">
                        {event.is_full ? <FiX /> : <FiArrowRight />}
                      </span>
                    </EventButton>
                  );
                })}
              </EventSlotGrid>
            </EventGroupBody>
          </EventGroup>
        );
      })}
    </EventList>
  );

  return (
    <>
      <RegularCourseIntro
        reducedMotion={prefersReducedMotion}
      />

      <PremiumApplySection
        id="public-event-apply"
        ref={ref}
        aria-labelledby="application-paths-title"
      >
      <Container>
        <Header
          data-public-event-apply-main
          variants={sectionRevealVariants}
          initial={prefersReducedMotion ? false : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.35 }}
        >
          <motion.div className="eyebrow" variants={sectionRevealItemVariants}>
            <span>02</span><i aria-hidden="true" /><b>Application</b>
          </motion.div>
          <motion.h2 id="application-paths-title" variants={sectionRevealItemVariants}>
            아이에게 맞는 시작을,<br /><span>지금 선택하세요.</span>
          </motion.h2>
          <motion.div className="header-note" variants={sectionRevealItemVariants}>
            <p>무료 OT로 먼저 경험하고, 상담에서 커리큘럼과 수강료를 안내받은 뒤 등록을 결정하세요. 부트캠프는 새로운 일정을 준비 중입니다.</p>
            <div className="choice-summary">
              <strong>지금 가능한 2가지</strong>
              <span>체험 · 정기과정 · Coming Soon</span>
            </div>
          </motion.div>
        </Header>

        <OfferGrid
          aria-labelledby="application-paths-title"
          variants={offerGridVariants}
          initial={prefersReducedMotion ? false : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, amount: 0.24 }}
        >
          <OfferCard
            className="offer-card--free"
            $tone="regular"
            aria-labelledby="free-ot-offer-title"
            variants={offerCardVariants}
            whileHover={prefersReducedMotion ? undefined : { y: -7, scale: 1.006 }}
          >
            <div className="offer-route"><span>01</span><small>먼저 경험하기</small></div>
            <div className="offer-top">
              <div>
                <div className="offer-kicker">Free Orientation</div>
                <h3 id="free-ot-offer-title">무료 OT부터</h3>
              </div>
              <span className="offer-icon" aria-hidden="true"><FiCheckCircle /></span>
            </div>
            <div className="offer-price-stack">
              <div className="price">OT 0원</div>
              <div className="original">결제 없이 온·오프라인 체험 · 등록 상담</div>
            </div>
            <p>온라인 또는 오프라인 OT 체험과 상담 후 4주 프로젝트 정기과정을 시작합니다.</p>
            <button type="button" className="offer-link" onClick={() => handleProgramTypeSelect('free', true)}>
              무료 OT부터 신청 <FiArrowRight aria-hidden="true" />
            </button>
          </OfferCard>

          <OfferCard
            className="offer-card--direct"
            $tone="direct"
            aria-labelledby="direct-offer-title"
            variants={offerCardVariants}
            whileHover={prefersReducedMotion ? undefined : { y: -7, scale: 1.006 }}
          >
            <div className="offer-route"><span>02</span><small>먼저 상담하기</small></div>
            <div className="offer-top">
              <div>
                <div className="offer-kicker">Course Consultation</div>
                <h3 id="direct-offer-title">정기과정 상담</h3>
              </div>
              <span className="offer-icon" aria-hidden="true"><FiZap /></span>
            </div>
            <div className="offer-price-stack">
              <div className="consultation-badge"><FiCheck aria-hidden="true" />아이에게 맞는 과정부터</div>
              <div className="price">충분히 듣고 결정하세요</div>
              <div className="original">커리큘럼 안내 후 수강료 상담</div>
            </div>
            <p>나니아톡·카카오톡에서 아이에게 맞는 커리큘럼과 수업 방식을 먼저 안내받고, 수강료를 확인한 뒤 등록을 결정하실 수 있습니다. 전화 상담은 카카오톡 문의 후 매니저와 시간을 조율해 진행합니다.</p>
            <div className="offer-mini-flow" aria-label="상담 후 등록 진행 단계">
              <span><b>01</b>커리큘럼 안내</span><i aria-hidden="true" />
              <span><b>02</b>수강료 확인</span><i aria-hidden="true" />
              <span><b>03</b>등록 결정</span>
            </div>
            <div className="consultation-actions">
              <button type="button" className="offer-link" onClick={props.onNarniaTalkOpen}>
                나니아톡 AI와 상담하기 <FiMessageCircle aria-hidden="true" />
              </button>
              <a className="offer-link offer-link--secondary" href="https://pf.kakao.com/_DZhmn" target="_blank" rel="noopener noreferrer">
                카카오톡으로 상담하기 <FiExternalLink aria-hidden="true" />
              </a>
            </div>
            <div className="registration-followup">
              상담을 마치셨다면
              <button type="button" onClick={handleDirectRegistration}>바로 등록하기</button>
            </div>
          </OfferCard>

          <OfferCard
            className="offer-card--bootcamp"
            $tone="bootcamp"
            aria-labelledby="bootcamp-offer-title"
            variants={offerCardVariants}
          >
            <div className="offer-route"><span>03</span><small>COMING SOON</small></div>
            <div className="offer-top">
              <div>
                <div className="offer-kicker">Bootcamp</div>
                <h3 id="bootcamp-offer-title">부트캠프</h3>
              </div>
              <span className="offer-icon" aria-hidden="true"><FiClock /></span>
            </div>
            <div className="offer-price-stack">
              <div className="price">COMING SOON</div>
              <div className="original">서울/경기 장소 미정 · 추후 공지</div>
            </div>
            <p>새로운 부트캠프 일정과 자세한 참가 안내를 준비하고 있습니다.</p>
            <button type="button" className="offer-link" disabled aria-disabled="true">
              일정 준비 중 <FiClock aria-hidden="true" />
            </button>
          </OfferCard>
        </OfferGrid>

        <Panel
          id="application-schedules"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 46, scale: 0.985 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.08 }}
          transition={{ duration: 0.86, ease: MOTION_EASE }}
        >
          <Row className="g-0">
            <Col lg={12}>
              <ListShell>
                <ColumnHeading>
                  <div>
                    <div className="title">신청 가능한 일정</div>
                    <div className="desc">신청 유형을 고른 뒤 원하는 날짜와 시간을 누르세요.</div>
                  </div>
                </ColumnHeading>

                <ParentConsultation />

                {!loading && !loadError && events.length > 0 && (
                  <ProgramSwitch role="tablist" aria-label="신청 유형 선택">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeProgramType === 'free'}
                      onClick={() => handleProgramTypeSelect('free')}
                    >
                      <span className="switch-label">무료 OT<small>정기과정 시작</small></span>
                      <span className="switch-count">{freeEventCount}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected="false"
                      aria-disabled="true"
                      disabled
                    >
                      <span className="switch-label">부트캠프<small>COMING SOON</small></span>
                      <span className="switch-count" aria-hidden="true"><FiClock /></span>
                    </button>
                  </ProgramSwitch>
                )}

                {!loading && !loadError && events.length > 0 && (
                  <ScheduleToolbar>
                    <div className="toolbar-top">
                      <div className="overview">
                        <strong>{activeProgramType === 'paid' ? '부트캠프' : '무료 OT'} · 신청 가능 {availableEventCount}개</strong>
                        <span>같은 날짜의 시간대는 한 카드로 묶었습니다.</span>
                      </div>
                      <ScheduleSearch>
                        <FiSearch aria-hidden="true" />
                        <input
                          type="search"
                          value={eventQuery}
                          onChange={(event) => setEventQuery(event.target.value)}
                          placeholder="과정명, 지역, 날짜 검색"
                          aria-label="일정 검색"
                        />
                        {eventQuery && (
                          <button type="button" onClick={() => setEventQuery('')} aria-label="검색어 지우기">
                            <FiX aria-hidden="true" />
                          </button>
                        )}
                      </ScheduleSearch>
                    </div>
                    <div className="filter-row">
                      <span className="filter-label"><FiMapPin aria-hidden="true" /> 지역</span>
                      <ScheduleFilters role="group" aria-label="일정 지역 필터">
                        {eventCategoryOptions.map((option) => {
                          const active = activeEventCategory === option.label;
                          return (
                            <ScheduleFilterButton
                              key={option.label}
                              type="button"
                              $active={active}
                              aria-pressed={active}
                              onClick={() => setActiveEventCategory(option.label)}
                            >
                              {option.label}<span>{option.count}</span>
                            </ScheduleFilterButton>
                          );
                        })}
                      </ScheduleFilters>
                    </div>
                  </ScheduleToolbar>
                )}

                {!loading && !loadError && events.length > 0 && groupedEvents.length > 0 && (
                  <ScheduleResultMeta role="status" aria-label="일정 검색 결과" aria-live="polite">
                    <span>
                      <strong>{filteredEvents.length}개 일정</strong>을 {groupedEvents.length}개 날짜 카드로 표시합니다.
                    </span>
                    <span>{activeProgramType === 'paid' ? '결제 후 참가 확정' : '결제 없이 신청 확정'}</span>
                  </ScheduleResultMeta>
                )}

                {loading && (
                  <ScheduleSkeleton role="status" aria-label="일정을 불러오는 중">
                    <span className="visually-hidden">신청 가능한 일정을 불러오고 있습니다.</span>
                    <div className="skeleton-row" aria-hidden="true" />
                    <div className="skeleton-row" aria-hidden="true" />
                    <div className="skeleton-row" aria-hidden="true" />
                  </ScheduleSkeleton>
                )}

                {!loading && loadError && (
                  <EmptyState role="alert">
                    <span className="state-icon"><FiAlertCircle aria-hidden="true" /></span>
                    <strong>일정을 불러오지 못했습니다</strong>
                    <p>{loadError}</p>
                    <div className="state-actions">
                      <StateAction type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                        <FiRefreshCw aria-hidden="true" /> 다시 불러오기
                      </StateAction>
                      {activeProgramType === 'free' && <OtConsultationLink />}
                    </div>
                  </EmptyState>
                )}

                {!loading && !loadError && programEvents.length === 0 && (
                  <EmptyState role="status">
                    <span className="state-icon"><FiCalendar aria-hidden="true" /></span>
                    <strong>{activeProgramType === 'free'
                      ? '현재 신청 가능한 무료 OT 일정이 없습니다'
                      : '현재 신청 가능한 일정이 없습니다'}</strong>
                    {activeProgramType === 'free' ? (
                      <>
                        <p>원하시는 체험 일정과 수업 방식을 카카오톡 채널에서 상담해 보세요.</p>
                        <OtConsultationLink />
                      </>
                    ) : (
                      <p>새로운 일정이 열리면 이곳에서 바로 확인할 수 있습니다.</p>
                    )}
                  </EmptyState>
                )}

                {!loading && !loadError && programEvents.length > 0 && groupedEvents.length === 0 && (
                  <EmptyState role="status">
                    <span className="state-icon"><FiSearch aria-hidden="true" /></span>
                    <strong>조건에 맞는 일정이 없습니다</strong>
                    <p>검색어나 선택한 지역을 바꾸면 다른 일정을 확인할 수 있습니다.</p>
                    <StateAction type="button" onClick={resetEventFilters}>전체 일정 보기</StateAction>
                  </EmptyState>
                )}

                {!loading && !loadError && activeProgramType === 'free' && groupedEvents.length > 0 && (
                  <ScheduleCollection
                    id="free-ot-schedules"
                    $tone="free"
                    aria-labelledby="free-ot-schedules-title"
                  >
                    <div className="collection-head">
                      <div>
                        <div className="collection-kicker">Free Orientation</div>
                        <h3 id="free-ot-schedules-title">무료 OT 일정</h3>
                        <p className="collection-description">결제 없이 신청하고, OT 이후 상담에서 커리큘럼과 수강료를 안내받은 뒤 정기과정 등록을 결정합니다.</p>
                      </div>
                      <span className="collection-price">무료 · 0원</span>
                    </div>
                    {renderEventGroups(groupedEvents)}
                  </ScheduleCollection>
                )}

                {!loading && !loadError && activeProgramType === 'paid' && groupedEvents.length > 0 && (
                  <ScheduleCollection
                    id="paid-bootcamp-schedules"
                    $tone="paid"
                    aria-labelledby="paid-bootcamp-schedules-title"
                  >
                    <div className="collection-head">
                      <div>
                        <div className="collection-kicker">Bootcamp</div>
                        <h3 id="paid-bootcamp-schedules-title">부트캠프 일정</h3>
                        <p className="collection-description">신청 후 my-page에서 결제를 완료하면 참가가 확정됩니다.</p>
                      </div>
                    </div>
                    {renderEventGroups(groupedEvents)}
                  </ScheduleCollection>
                )}

                {!loading && !loadError && activeProgramType === 'free'
                  && groupedEvents.length > 0 && availableEventCount === 0 && (
                  <EmptyState role="status">
                    <span className="state-icon"><FiCalendar aria-hidden="true" /></span>
                    <strong>공개된 무료 OT 일정이 모두 마감되었습니다</strong>
                    <p>다음 체험 일정이 궁금하시면 카카오톡 채널에서 상담해 보세요.</p>
                    <OtConsultationLink />
                  </EmptyState>
                )}

                {!loading && !loadError && events.length > 0 && <MyPageNote>
                  {activeProgramType === 'paid'
                    ? '부트캠프는 신청 후 생성한 계정으로 my-page에 로그인해 결제를 완료하면 확정됩니다. '
                    : '무료 OT는 결제 없이 신청이 확정됩니다. '}
                  신청 내역은{' '}
                  <a href="/my-page/" target="_blank" rel="noopener noreferrer">
                    my-page에서 확인 <FiExternalLink />
                  </a>
                  할 수 있습니다.
                </MyPageNote>}
              </ListShell>
            </Col>
          </Row>
        </Panel>
      </Container>
    </PremiumApplySection>
    <PremiumPublicApplyModal
      show={showApplyModal}
      animation={!prefersReducedMotion}
      onHide={() => {
        setShowApplyModal(false);
        setFieldErrors({});
      }}
      centered
      scrollable
      size="lg"
      aria-labelledby="public-apply-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="public-apply-modal-title">
          <ModalTitleBlock>
            <div className="eyebrow"><FiCalendar /> Narnia Course Apply</div>
            <h3>
              {isDirectRegistration
                ? '청소년 정기과정 바로 등록'
                : selectedEvent ? `${getPublicEventTitle(selectedEvent.title)} 신청` : '나니아 과정 일정 신청'}
            </h3>
            <p>
              {isDirectRegistration
                ? '상담에서 커리큘럼과 수강료를 안내받으셨다면 계정을 만들고 결제로 이어갈 수 있습니다. 결제 완료 시 멤버십 등록이 확정됩니다.'
                : isBootcampFlow
                ? '기존 신청서를 제출하면 계정이 함께 만들어집니다. 신청 후 my-page에 로그인해 결제를 완료하면 참가가 최종 확정되고 서버 실제 사용시간 5시간이 지급됩니다.'
                : '선택한 일정에 신청하면서 자녀가 my-page와 AI server에서 사용할 계정을 함께 만듭니다.'}
            </p>
          </ModalTitleBlock>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isDirectRegistration || selectedEvent ? (
          renderApplicationForm()
        ) : (
          <SelectionPrompt
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <span className="icon"><FiCalendar /></span>
            <strong>먼저 일정을 선택해 주세요</strong>
            신청 가능한 일정 카드 중 하나를 선택하면 신청서를 작성할 수 있습니다.
          </SelectionPrompt>
        )}
      </Modal.Body>
    </PremiumPublicApplyModal>

    <TermsModal show={showTerms} onHide={() => setShowTerms(false)} />
    <PrivacyModal show={showPrivacy} onHide={() => setShowPrivacy(false)} />
    </>
  );
});

export default PublicEventApply;
